import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { marked } from 'marked';
import { useLanguage } from '../context/LanguageContext';
import { studentFetch } from '../utils/authFetch';
import * as THREE from 'three';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

marked.setOptions({ breaks: true, gfm: true });

function renderMarkdown(text) {
  return { __html: marked.parse(text || '') };
}

/* ─── i18n ─────────────────────────────────────────────────────────────── */
const T = {
  ar: {
    loading:          'جارٍ تحضير الشرح…',
    yes_clear:        'نعم، فهمت 👍',
    no_help:          'أحتاج مساعدة',
    ask_understood:   'هل فهمت الشرح؟',
    checking:         'ممتاز! سنتحقق من فهمك الآن…',
    quiz_loading:     'جاري تحضير الأسئلة…',
    question_of:      (n, total) => `السؤال ${n} من ${total}`,
    submit:           'تأكيد الإجابة',
    correct:          'أحسنت! اجتزت التحقق 🎉',
    fail_encourage:   'لا بأس! الأخطاء طريق التعلم. دعنا نراجع الشرح مرة أخرى 💪',
    retry_explain:    'إعادة الشرح',
    session_complete: 'أحسنت! أتممت هذا الموضوع 🌟',
    next_topic:       'موضوع جديد',
    back_subjects:    'العودة للمواد',
    true_btn:         'صح',
    false_btn:        'خطأ',
    type_answer:      'اكتب إجابتك هنا…',
    toggle_lang:      'EN',
    error_explain:    'حدث خطأ في تحميل الشرح.',
    retry:            'إعادة المحاولة',
    chat_ph:          'اكتب سؤالك هنا…',
    send:             'إرسال',
    understood_now:   'فهمت الآن — تحقق من فهمي',
    error_chat:       'حدث خطأ. حاول مرة أخرى.',
    img_enlarge:      'اضغط لتكبير الصورة 🔍',
    topic_progress_label:  (current, total) => `الموضوع ${current} من ${total}`,
    topics_progress_title: 'تقدمك في المواضيع',
    topic_done_title:      'أحسنت! أتممت هذا الموضوع 🌟',
    next_topic_btn:        (name) => `الموضوع التالي: ${name} ←`,
    session_complete_title: 'أتممت الجلسة!',
    stat_topics:           'مواضيع',
    stat_xp:               'نقاط XP',
    stat_avg_score:        'متوسط التحقق',
    back_to_units:         'العودة لقائمة الوحدات',
    inter_topic_congrats:    'أحسنت! أتممت هذا الموضوع',
    inter_topic_score_label: 'نتيجة التحقق',
    inter_topic_xp_label:    'نقاط مكتسبة',
    continue_to_next:        'متابعة للموضوع التالي ←',
    finish_session_btn:      'إنهاء الجلسة',
  },
  en: {
    loading:          'Preparing explanation…',
    yes_clear:        'Yes, I got it 👍',
    no_help:          'I need help',
    ask_understood:   'Did you understand the explanation?',
    checking:         'Great! Let\'s check your understanding…',
    quiz_loading:     'Preparing questions…',
    question_of:      (n, total) => `Question ${n} of ${total}`,
    submit:           'Submit Answer',
    correct:          'Well done! You passed 🎉',
    fail_encourage:   "No worries! Mistakes help us learn. Let's review the explanation together 💪",
    retry_explain:    'Review Again',
    session_complete: 'Well done! Topic complete 🌟',
    next_topic:       'New Topic',
    back_subjects:    'Back to Subjects',
    true_btn:         'True',
    false_btn:        'False',
    type_answer:      'Type your answer here…',
    toggle_lang:      'AR',
    error_explain:    'Failed to load explanation.',
    retry:            'Retry',
    chat_ph:          'Type your question…',
    send:             'Send',
    understood_now:   'I understand now — check my understanding',
    error_chat:       'An error occurred. Please try again.',
    img_enlarge:      'Tap to enlarge 🔍',
    topic_progress_label:  (current, total) => `Topic ${current} of ${total}`,
    topics_progress_title: 'Your topics progress',
    topic_done_title:      'Well done! Topic complete 🌟',
    next_topic_btn:        (name) => `Next topic: ${name} →`,
    session_complete_title: 'Session complete!',
    stat_topics:           'Topics',
    stat_xp:               'XP Points',
    stat_avg_score:        'Avg Score',
    back_to_units:         'Back to Units',
    inter_topic_congrats:    'Well done! Topic complete',
    inter_topic_score_label: 'Score',
    inter_topic_xp_label:    'XP earned',
    continue_to_next:        'Continue to next topic →',
    finish_session_btn:      'Finish session',
  },
};

/* ─── States ─────────────────────────────────────────────────────────────── */
const STATE = {
  WAITING:        'WAITING',
  STREAMING:      'STREAMING',
  CLEAR_Q:        'CLEAR_Q',
  QUIZ:           'QUIZ',
  COMPLETE:       'COMPLETE',
  FINAL_SUMMARY:  'FINAL_SUMMARY',
  HELP:           'HELP',
  ERROR:          'ERROR',
  FAIL:           'FAIL',
};

function newUUID() {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ExplanationScreen  — two-panel layout
═══════════════════════════════════════════════════════════════════════════ */
export default function ExplanationScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state || {};

  const studentRaw  = JSON.parse(localStorage.getItem('student_data') || 'null');
  const token       = localStorage.getItem('student_token') || '';
  const personaRaw  = JSON.parse(localStorage.getItem('jahbath_student_persona') || 'null');
  const { lang: storedLang, setLang: setLang_ctx } = useLanguage();
  const studentName = localStorage.getItem('jahbath_student_name') || null;

  const student_id = navState.student_id || studentRaw?.id || '';
  const subject    = navState.subject    || '';
  const unit       = navState.unit       || '';
  // Phase A.2: topic is now derived from the queue's current index.
  // Fallback chain preserves legacy single-topic behavior.
  const topic = (navState.selected_topics && navState.selected_topics.length > 0)
    ? (navState.selected_topics[0] || '')
    : (navState.topic || '');
  const topicList  = navState.topic_list || [];
  // Multi-topic session support (Phase A.1 scaffolding — inactive logic)
  const selectedTopicsInit = (navState.selected_topics && navState.selected_topics.length > 0)
    ? navState.selected_topics
    : (navState.topic ? [navState.topic] : []);
  const grade      = navState.grade      || parseInt(localStorage.getItem('jahbath_student_grade') || '7', 10);
  const resumeId   = navState.session_id || null;

  const favorite_character   = personaRaw?.favorite_character || '';
  const favorite_story_style = personaRaw?.story_style        || '';

  const ARABIC_ONLY_SUBJECTS = [
    'التربية الإسلامية',
    'اللغة العربية',
    'تاريخ قطر والمواطنة',
  ];
  const SUBJECT_LANG_FIELD = {
    'التربية الإسلامية':   'islamic_lang',
    'اللغة العربية':       'arabic_lang',
    'تاريخ قطر والمواطنة': 'qatar_history_lang',
  };
  const currentSubject      = navState?.subject || '';
  const effectiveLang       = ARABIC_ONLY_SUBJECTS.includes(currentSubject)
    ? (studentRaw?.[SUBJECT_LANG_FIELD[currentSubject]] || 'ar')
    : storedLang;
  const isArabicOnlySubject = ARABIC_ONLY_SUBJECTS.includes(currentSubject);

  /* ── state ── */
  const [lang,         setLang]         = useState(effectiveLang);
  const [screenState,  setScreenState]  = useState(STATE.WAITING);
  const [messages,     setMessages]     = useState([]);
  const [sessionId, setSessionId]       = useState(resumeId || newUUID());
  const [learningObjs, setLearningObjs] = useState([]);
  const [errorMsg,     setErrorMsg]     = useState('');

  // Quiz
  const [questions,      setQuestions]      = useState([]);
  const [finalScore,     setFinalScore]     = useState(0);
  const [currentLOIndex, setCurrentLOIndex] = useState(0);
  const [conceptScores,  setConceptScores]  = useState([]);
  const currentLOIndexRef = useRef(0);
  const learningObjsLengthRef = useRef(0);

  // Quiz modal
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizModalOpen, setQuizModalOpen] = useState(false);
  const [quizDone,      setQuizDone]      = useState(false);
  const [quizResults,   setQuizResults]   = useState(null);
  const [needsReExplain, setNeedsReExplain] = useState(false);

  // Chat bar
  const [chatInput,    setChatInput]    = useState('');
  const [chatSending,  setChatSending]  = useState(false);

  // Stopwatch
  const [elapsedSecs, setElapsedSecs] = useState(0);
  const timerRef = useRef(null);

  // Lightbox
  const [lightboxImage, setLightboxImage] = useState(null);

  // Re-explanation
  const [reExplainCount, setReExplainCount] = useState(0);
  const [awaitingOwnStyle, setAwaitingOwnStyle] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  // Multi-topic session state (Phase A.1 — not yet consumed by logic)
  const [topicsQueue]         = useState(selectedTopicsInit);
  const [currentTopicIndex, setCurrentTopicIndex] = useState(0);
  const [topicResults, setTopicResults] = useState([]);
  const currentTopicIndexRef  = useRef(0);

  // XP + shimmer
  const [totalXP, setTotalXP] = useState(0);
  const [showShimmer, setShowShimmer] = useState(false);
  const [xpFloat, setXpFloat] = useState(null);
  const chatScrollRef = useRef(null);

  // Key terms (FIX 5)
  const [keyTerms, setKeyTerms] = useState([]);

  // Poem display
  const [poemVerses, setPoemVerses] = useState([]);
  const [poemOpen,   setPoemOpen]   = useState(true);

  // Owl speech bubble
  const [owlSpeechMsg, setOwlSpeechMsg] = useState('');

  // Live stream text — dedicated state for real-time markdown rendering
  const [streamText, setStreamText] = useState('');

  // Explanation image frame
  const [explImageSrc, setExplImageSrc]       = useState(null);
  const [isImageLoading, setIsImageLoading]   = useState(false);

  // Fast Track Mode
  const [fastTrackActive, setFastTrackActive] = useState(false);
  const consecutiveFailsRef = useRef(0);

  // Intensive review
  const [sessionMode,          setSessionMode]          = useState('normal');
  const [daysToExam,           setDaysToExam]           = useState(null);
  const [examSessionStructure, setExamSessionStructure] = useState({});

  const canvasRef    = useRef(null);
  const scrollRef    = useRef(null);
  const abortRef           = useRef(null);
  const liveStreamMsgKeyRef = useRef(null);
  const msgKeyRef    = useRef(0);
  const chatInputRef = useRef(null);
  const textBufferRef = useRef("");
  const currentTopicOverrideRef = useRef(null);

  /* ── Three.js background ── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 100);
    camera.position.z = 7;
    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const L1 = new THREE.DirectionalLight(0xffffff, 1.2); L1.position.set(6, 8, 4); scene.add(L1);
    const L2 = new THREE.DirectionalLight(0xffffff, 0.6); L2.position.set(-5, -3, 4); scene.add(L2);
    const meshes = [];
    [
      [new THREE.IcosahedronGeometry(0.36, 0), -4, 2.5, -1.2],
      [new THREE.IcosahedronGeometry(0.24, 0), 3.6, 2, -2],
      [new THREE.OctahedronGeometry(0.30, 0), -3, -1.8, -1.5],
      [new THREE.OctahedronGeometry(0.20, 0), 2.8, -2.2, -1],
      [new THREE.TetrahedronGeometry(0.26, 0), 0.7, 2.8, -1.8],
      [new THREE.TetrahedronGeometry(0.16, 0), -1.4, -2.6, -1.4],
      [new THREE.IcosahedronGeometry(0.14, 0), 4, 0.5, -0.6],
      [new THREE.OctahedronGeometry(0.12, 0), -4, -0.7, -0.6],
      [new THREE.IcosahedronGeometry(0.11, 0), 1.8, -3, -0.9],
      [new THREE.TetrahedronGeometry(0.13, 0), -2, 3, -0.9],
    ].forEach(([G, x, y, z]) => {
      const mat = new THREE.MeshPhongMaterial({ color: 0x34D399, transparent: true, opacity: 0.22, shininess: 120, specular: new THREE.Color(0xffffff) });
      const m = new THREE.Mesh(G, mat);
      m.position.set(x, y, z);
      m.rotation.set(Math.random() * Math.PI * 2, Math.random() * Math.PI * 2, Math.random() * Math.PI * 2);
      m._oy = y; m._fs = 0.2 + Math.random() * 0.5; m._fa = 0.12 + Math.random() * 0.22;
      m._rx = (0.002 + Math.random() * 0.006) * (Math.random() > 0.5 ? 1 : -1);
      m._ry = (0.002 + Math.random() * 0.005) * (Math.random() > 0.5 ? 1 : -1);
      m._ph = Math.random() * Math.PI * 2;
      scene.add(m); meshes.push(m);
    });
    const PC = 120;
    const pp = new Float32Array(PC * 3);
    const pv = [];
    for (let i = 0; i < PC; i++) {
      pp[i * 3] = (Math.random() - 0.5) * 16;
      pp[i * 3 + 1] = (Math.random() - 0.5) * 12;
      pp[i * 3 + 2] = (Math.random() - 0.5) * 5 - 1.5;
      pv.push({ vx: (Math.random() - 0.5) * 0.007, vy: (Math.random() - 0.5) * 0.007 });
    }
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute('position', new THREE.BufferAttribute(pp, 3));
    const pMat = new THREE.PointsMaterial({ color: 0x34D399, size: 0.07, transparent: true, opacity: 0.5, sizeAttenuation: true });
    scene.add(new THREE.Points(pGeo, pMat));
    const lp = new Float32Array(PC * PC * 6);
    const lGeo = new THREE.BufferGeometry();
    lGeo.setAttribute('position', new THREE.BufferAttribute(lp, 3));
    const lMesh = new THREE.LineSegments(lGeo, new THREE.LineBasicMaterial({ color: 0x34D399, transparent: true, opacity: 0.1 }));
    scene.add(lMesh);
    let mx = 0, my = 0;
    const onMM = (e) => { mx = (e.clientX / window.innerWidth - 0.5) * 2; my = (e.clientY / window.innerHeight - 0.5) * 2; };
    window.addEventListener('mousemove', onMM);
    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', onResize);
    let t = 0, li = 0, animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      t += 0.007;
      meshes.forEach(m => {
        m.rotation.x += m._rx; m.rotation.y += m._ry;
        m.position.y = m._oy + Math.sin(t * m._fs + m._ph) * m._fa;
      });
      for (let i = 0; i < PC; i++) {
        pp[i * 3] += pv[i].vx; pp[i * 3 + 1] += pv[i].vy;
        if (Math.abs(pp[i * 3]) > 8) pv[i].vx *= -1;
        if (Math.abs(pp[i * 3 + 1]) > 6) pv[i].vy *= -1;
      }
      pGeo.attributes.position.needsUpdate = true;
      li = 0;
      for (let i = 0; i < PC; i++) for (let j = i + 1; j < PC; j++) {
        const dx = pp[i * 3] - pp[j * 3], dy = pp[i * 3 + 1] - pp[j * 3 + 1];
        if (dx * dx + dy * dy < 5) {
          lp[li++] = pp[i * 3]; lp[li++] = pp[i * 3 + 1]; lp[li++] = pp[i * 3 + 2];
          lp[li++] = pp[j * 3]; lp[li++] = pp[j * 3 + 1]; lp[li++] = pp[j * 3 + 2];
        }
      }
      lGeo.attributes.position.needsUpdate = true;
      lGeo.setDrawRange(0, li / 3);
      camera.position.x += (mx * 0.3 - camera.position.x) * 0.032;
      camera.position.y += (-my * 0.18 - camera.position.y) * 0.032;
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
    };
    animate();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('mousemove', onMM);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
    };
  }, []);

  const t   = T[lang];
  const dir = lang === 'ar' ? 'rtl' : 'ltr';

  const owlBaseAnim = screenState === STATE.WAITING   ? 'es-owl-think'
    : screenState === STATE.STREAMING ? 'es-owl-teach'
    : screenState === STATE.COMPLETE  ? 'es-owl-celeb'
    : 'es-owl-idle';
  const owlAnimClass = dir === 'ltr' ? `${owlBaseAnim} es-owl-flipped` : owlBaseAnim;

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setElapsedSecs(s => s + 1);
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  const fmtTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return String(m).padStart(2, '0') + ':' + String(s).padStart(2, '00');
  };

  const showChatBar = [STATE.STREAMING, STATE.CLEAR_Q, STATE.HELP].includes(screenState);

  /* ── auto-scroll ── */
  useEffect(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
      if (chatScrollRef.current) {
        chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
      }
    });
  }, [messages, screenState]);

  /* ── redirect guard ── */
  useEffect(() => {
    if (!student_id || !subject || !unit || !topic) {
      navigate('/student/subjects', { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── fetch poem verses when this unit has a memorization poem ── */
  useEffect(() => {
    if (!navState.has_poem || !navState.poem_title) return;
    const params = new URLSearchParams({
      grade:      String(navState.grade || grade),
      subject:    navState.subject || subject,
      poem_title: navState.poem_title,
    });
    studentFetch(`${API}/student/poem-verses?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res?.json())
      .then(data => { if (Array.isArray(data?.verses)) setPoemVerses(data.verses); })
      .catch(err => console.error('poem-verses fetch failed:', err));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── bootstrap ── */
  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      try {
        let objs = [];
        let tl = [];

        if (navState.learning_objectives?.length > 0) {
          objs = navState.learning_objectives;
          tl = navState.topic_list?.length > 0 ? navState.topic_list : [];
        } else {
          const sliceRes = await studentFetch(`${API}/registry/slice`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body:    JSON.stringify({ student_id, requesting_agent: 'explainer', subject, unit, unit_id: navState?.unit_id ?? null, topic, grade, student_profile: {} }),
          });
          if (!sliceRes) return;
          if (!cancelled) {
            const sliceData = await sliceRes.json();
            const sliceLO = sliceData?.payload?.learning_objectives || sliceData?.learning_objectives || sliceData?.objectives || [];
            const sliceTL = sliceData?.payload?.topic_list || sliceData?.topic_list || [];
            objs = sliceLO;
            tl = sliceTL.length > 0 ? sliceTL : (navState.topic_list?.length > 0 ? navState.topic_list : []);
          }
        }

        if (!cancelled) {
          setLearningObjs(objs);
          learningObjsLengthRef.current = objs.length;
          startExplain([objs[0]], null, objs);
        }
      } catch {
        if (!cancelled) { setErrorMsg(t.error_explain); setScreenState(STATE.ERROR); }
      }
    }
    bootstrap();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── start explain SSE ── */
  const startExplain = useCallback(async (objs, reexplain_style = null, allObjs = null) => {
    textBufferRef.current = "";
    liveStreamMsgKeyRef.current = null;
    setStreamText('');
    setShowShimmer(true);
    setScreenState(STATE.STREAMING);
    setExplImageSrc(null);
    setIsImageLoading(false);
    setMessages(prev => {
      if (prev.length === 0) return [];
      const last = prev[prev.length - 1];
      if (last && last.type === 'divider') return prev;
      return [
        ...prev,
        { type: 'divider', topic: currentTopicOverrideRef.current || topic, id: `divider-${Date.now()}` },
      ];
    });
    setQuestions([]);

    const body = {
      student_id, session_id: sessionId, subject, subject_id: navState?.subject_id || null, unit,
      unit_id: navState?.unit_id ?? null,
      topic: currentTopicOverrideRef.current || topic,
      grade,
      learning_objectives: objs, delivery_language: lang,
      student_level: 'medium', favorite_character, favorite_story_style, stream: true,
      reexplain_style,
      student_name: studentName,
      generate_images: !isArabicOnlySubject,
      lo_index: currentLOIndexRef.current,
      is_last_lo: currentLOIndexRef.current >= learningObjsLengthRef.current - 1,
      all_learning_objectives: currentLOIndexRef.current === 0 ? (allObjs || learningObjs) : null,
      previous_lo_summary: currentLOIndexRef.current > 0 ? (textBufferRef.current || "").slice(-500) : null,
      poem_title: navState.poem_title || null,
    };
    let silenceTimer = null;
    const resetSilenceTimer = () => {
      clearTimeout(silenceTimer);
      silenceTimer = setTimeout(() => {
        abortRef.current?.abort();
        setErrorMsg(t.error_explain);
        setScreenState(STATE.ERROR);
      }, 60000);
    };
    let streamEndReceived = false;
    try {
      abortRef.current = new AbortController();
      const res = await studentFetch(`${API}/session/explain`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify(body),
        signal:  abortRef.current.signal,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      if (!res.body) throw new Error('No body');

      const reader  = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      resetSilenceTimer();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        resetSilenceTimer();
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data:')) continue;
          const raw = line.slice(5).trim();
          if (!raw) continue;
          try {
            const parsed = JSON.parse(raw);
            if (parsed.type === 'status' && parsed.event === 'stream_end') streamEndReceived = true;
            handleChunk(parsed);
          } catch { /* ignore */ }
        }
      }
      clearTimeout(silenceTimer);
      if (!streamEndReceived) {
        setErrorMsg(t.error_explain);
        setScreenState(STATE.ERROR);
      }
    } catch (err) {
      clearTimeout(silenceTimer);
      if (err.name === 'AbortError') return;
      setErrorMsg(t.error_explain);
      setScreenState(STATE.ERROR);
    }
  }, [sessionId, lang, subject, unit, topic, grade, student_id, token, favorite_character, favorite_story_style]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Phase A.2: advance to the next topic in the queue ── */
  const advanceToNextTopic = useCallback(async () => {
    // Save current topic's result
    const current = topicsQueue[currentTopicIndexRef.current] || topic;
    setTopicResults(prev => [...prev, {
      topic: current,
      score: Math.round(finalScore || 0),
      xp: totalXP,
      completed_at: Date.now(),
    }]);

    const nextIndex = currentTopicIndexRef.current + 1;

    // Last topic already done → final summary
    if (nextIndex >= topicsQueue.length) {
      setScreenState(STATE.FINAL_SUMMARY);
      return;
    }

    // Advance pointer
    currentTopicIndexRef.current = nextIndex;
    setCurrentTopicIndex(nextIndex);

    const nextTopic = topicsQueue[nextIndex];

    // Reset per-topic state (does NOT touch session_id, topicsQueue, topicResults, totalXP)
    setMessages([]);
    setLearningObjs([]);
    learningObjsLengthRef.current = 0;
    setCurrentLOIndex(0);
    currentLOIndexRef.current = 0;
    setConceptScores([]);
    setFinalScore(0);
    setQuestions([]);
    setQuizQuestions([]);
    setQuizDone(false);
    setQuizResults(null);
    setNeedsReExplain(false);
    setReExplainCount(0);
    setStreamText('');
    textBufferRef.current = '';
    setExplImageSrc(null);
    setIsImageLoading(false);
    setScreenState(STATE.WAITING);

    // Re-bootstrap for the next topic (mirrors the existing bootstrap useEffect logic)
    try {
      const sliceRes = await studentFetch(`${API}/registry/slice`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify({
          student_id, requesting_agent: 'explainer',
          subject, unit, unit_id: navState?.unit_id ?? null,
          topic: nextTopic,
          grade, student_profile: {}
        }),
      });
      if (!sliceRes) return;
      const sliceData = await sliceRes.json();
      const sliceLO = sliceData?.payload?.learning_objectives || sliceData?.learning_objectives || sliceData?.objectives || [];
      const objs = sliceLO;
      setLearningObjs(objs);
      learningObjsLengthRef.current = objs.length;

      // Trigger the existing startExplain for the first LO of the new topic
      currentTopicOverrideRef.current = nextTopic;
      startExplain([objs[0]], null, objs);
    } catch {
      setErrorMsg(t.error_explain);
      setScreenState(STATE.ERROR);
    }
  }, [
    topicsQueue, topic, finalScore, totalXP,
    student_id, subject, unit, grade, token, t, startExplain,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleChunk(chunk) {
    if (chunk.type === 'text') {
      setShowShimmer(false);
      setMessages(prev => prev.filter(m => m.type !== 'retrying'));
      textBufferRef.current += chunk.content;
      setStreamText(textBufferRef.current);
      const termMatches = chunk.content.match(/\*\*([^*]+)\*\*/g);
      if (termMatches) {
        const newTerms = termMatches.map(m => m.replace(/\*\*/g, ''));
        setKeyTerms(prev => {
          const merged = [...prev];
          newTerms.forEach(t => { if (!merged.includes(t)) merged.push(t); });
          return merged;
        });
      }
      const key = ++msgKeyRef.current;
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last && last.role === 'ai' && last.type === 'text') {
          const updated = [...prev];
          updated[updated.length - 1] = { ...last, content: last.content + chunk.content };
          return updated;
        }
        liveStreamMsgKeyRef.current = key;
        return [...prev, { role: 'ai', type: 'text', content: chunk.content, key }];
      });
    } else if (chunk.type === 'image') {
      setExplImageSrc(chunk.content);
      setIsImageLoading(false);
    } else if (chunk.type === 'kp') {
      const kpKey = ++msgKeyRef.current;
      setMessages(prev => [...prev, { role: 'ai', type: 'kp', content: chunk.content, key: kpKey }]);
    } else if (chunk.type === 'status') {
      if (chunk.event === 'image_generating') {
        setIsImageLoading(true);
      } else if (chunk.event === 'image_failed') {
        setIsImageLoading(false);
      } else if (chunk.event === 'retrying') {
        setMessages(prev => {
          const filtered = prev.filter(m => m.type !== 'retrying');
          return [...filtered, {
            role: 'ai',
            type: 'retrying',
            key: Date.now()
          }];
        });
      } else if (chunk.event === 'stream_end') {
        if (chunk.fast_track_eligible !== undefined) {
          const isIntensive = chunk.session_mode === 'intensive_review';
          setFastTrackActive(chunk.fast_track_eligible || isIntensive);
          consecutiveFailsRef.current = 0;
        }
        if (chunk.session_mode) {
          setSessionMode(chunk.session_mode);
        }
        if (chunk.days_to_exam !== undefined) {
          setDaysToExam(chunk.days_to_exam);
        }
        if (chunk.exam_session_structure &&
            Object.keys(chunk.exam_session_structure).length > 0) {
          setExamSessionStructure(chunk.exam_session_structure);
        }
        setScreenState(STATE.CLEAR_Q);
      }
    } else if (chunk.type === 'error') {
      setErrorMsg(chunk.message || t.error_explain);
      setScreenState(STATE.ERROR);
    }
  }

  /* ── foundation review ── */
  function triggerFoundationReview() {
    setMessages(prev => [...prev, {
      role: 'ai',
      type: 'text',
      content: lang === 'ar'
        ? 'لقد استنفدنا أساليب الشرح المتاحة. أنصحك بمراجعة المفاهيم الأساسية السابقة أولاً.'
        : "We've used all available explanation styles. I recommend reviewing the foundational concepts first.",
      key: ++msgKeyRef.current,
    }]);
    setScreenState(STATE.HELP);
    setTimeout(() => navigate('/student/dashboard'), 4000);
  }

  /* ── yes / no ── */
  function handleYesClear() { fetchQuiz(); }
  function handleNoHelp() {
    if (reExplainCount >= 2) {
      triggerFoundationReview();
      return;
    }

    const gradeNum = parseInt(grade) || 7;
    let styleOptions;
    if (gradeNum <= 6) {
      styleOptions = lang === 'ar'
        ? ['قصة خيالية', 'مثال من الحياة اليومية', 'تشبيه بسيط', 'سيناريو الشخصية المفضلة', 'قصة يرويها جهبذ', 'بأسلوبك أنت 🎤']
        : ['Fantasy story', 'Everyday life example', 'Simple analogy', 'Favorite character scenario', 'Story narrated by Jahbath', 'My own style 🎤'];
    } else if (gradeNum <= 9) {
      styleOptions = lang === 'ar'
        ? ['سيناريو من الواقع', 'تطبيق عملي', 'سيناريو الشخصية المفضلة', 'قصة يرويها جهبذ', 'بأسلوبك أنت 🎤']
        : ['Real-world scenario', 'Practical application', 'Favorite character scenario', 'Story narrated by Jahbath', 'My own style 🎤'];
    } else {
      styleOptions = lang === 'ar'
        ? ['أسلوب الامتحان الرسمي', 'بأسلوبك أنت 🎤']
        : ['Formal exam style', 'My own style 🎤'];
    }

    setMessages(prev => [...prev, {
      role: 'ai',
      type: 'reexplain_prompt',
      styleOptions,
      key: ++msgKeyRef.current,
    }]);
    setScreenState(STATE.HELP);
  }

  async function handleReExplainStyle(style) {
    setReExplainCount(prev => prev + 1);
    setMessages(prev => [...prev, {
      role: 'user',
      type: 'text',
      content: style,
      key: ++msgKeyRef.current,
    }]);

    const isOwnStyle = style.includes('🎤');

    if (isOwnStyle) {
      setAwaitingOwnStyle(true);
      setMessages(prev => [...prev, {
        role: 'ai',
        type: 'text',
        content: lang === 'ar'
          ? 'أخبرني كيف تحب أن تتعلم؟ صف لي أسلوبك المفضل — مثلاً: "اشرح لي باستخدام توم وجيري" وسأشرح لك بنفس الطريقة 😊'
          : 'Tell me how you like to learn! For example: "Explain it using Tom and Jerry" — and I will adapt the explanation just for you 😊',
        key: ++msgKeyRef.current,
      }]);
      setScreenState(STATE.HELP);
      return;
    }

    startExplain([learningObjs[currentLOIndexRef.current]], style);
  }

  /* ── quiz result return handler ── */
  useEffect(() => {
    if (!location.state?.quiz_result) return;
    const { correctCount, avgScore, topic: quizTopic } = location.state.quiz_result;
    if (correctCount >= 2) {
      setConceptScores(prev => [...prev, { topic: quizTopic, score: Math.round(avgScore) }]);
      setFinalScore(avgScore);
      setScreenState(STATE.COMPLETE);
    } else {
      setScreenState(STATE.FAIL);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── fetch quiz ── */
  async function fetchQuiz() {
    const checkKey = ++msgKeyRef.current;
    setMessages(prev => [...prev, { role: 'ai', type: 'text', content: t.checking, key: checkKey }]);

    const loadKey = ++msgKeyRef.current;
    setMessages(prev => [...prev, { role: 'ai', type: 'quiz_loading', key: loadKey }]);
    setScreenState(STATE.QUIZ);

    try {
      const currentQuestionCount = (fastTrackActive && consecutiveFailsRef.current === 0) ? 1 : 3;
      const getDifficulty = () => {
        const dist = examSessionStructure?.difficulty_distribution;
        if (!dist) return 'medium';
        const rand = Math.random() * 100;
        if (rand < (dist.easy || 30)) return 'easy';
        if (rand < (dist.easy || 30) + (dist.medium || 50)) return 'medium';
        return 'hard';
      };
      const getDifficultyDistribution = () => {
        const dist = examSessionStructure?.difficulty_distribution;
        if (!dist) return null;
        const rand = Math.random() * 100;
        const easy = dist.easy || 30;
        const med  = dist.medium || 50;
        if (rand < easy)       return { easy: 1.0, medium: 0.0, hard: 0.0 };
        if (rand < easy + med) return { easy: 0.0, medium: 1.0, hard: 0.0 };
        return { easy: 0.0, medium: 0.0, hard: 1.0 };
      };
      const r = await studentFetch(`${API}/session/build-quiz`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify({
          student_id,
          session_id:          sessionId,
          subject,
          unit,
          topic,
          grade,
          exam_type:           'comprehension_check',
          question_count:      currentQuestionCount,
          difficulty_distribution: getDifficultyDistribution(),
          delivery_language:   lang,
          learning_objectives: learningObjs.length > 0 ? [learningObjs[currentLOIndexRef.current]].map(lo => lo.id || lo.description || String(lo)) : null,
          learning_objectives_full: learningObjs.length > 0 ? [learningObjs[currentLOIndexRef.current]] : null,
          unit_id: navState?.unit_id ?? null,
        }),
      });
      if (!r.ok || !r.body) throw new Error('stream failed');
      const reader = r.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data:')) continue;
          const raw = line.slice(5).trim();
          if (!raw) continue;
          try {
            const chunk = JSON.parse(raw);
            if (chunk.type === 'complete') {
              setMessages(prev => prev.filter(m => m.key !== loadKey));
              if (!chunk.questions?.length) throw new Error('No questions');
              setQuizQuestions(chunk.questions);
              setQuizModalOpen(true);
              return;
            } else if (chunk.type === 'error') {
              throw new Error(chunk.code || 'Quiz build error');
            }
          } catch (e) { if (e.message !== 'Quiz build error') continue; else throw e; }
        }
      }
      throw new Error('Stream ended without complete chunk');
    } catch {
      setMessages(prev => prev.filter(m => m.key !== loadKey));
      setErrorMsg(t.error_explain);
      setScreenState(STATE.ERROR);
    }
  }

  /* ── server-side grading ── */
  function gradeOnServer(qResults, studentId, sessionId, deliveryLang, subjectId) {
    Promise.allSettled(
      qResults.map(q =>
        studentFetch(`${API}/session/grade`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body:    JSON.stringify({
            student_id:        studentId,
            session_id:        sessionId,
            question_id:       q.question.question_id,
            question_type:     q.question.question_type,
            student_answer:    q.answer,
            grading_mode:      ['MCQ', 'TF', 'FIB', 'MAT'].includes(q.question.question_type) ? 'automated' : 'ai_evaluated',
            delivery_language: deliveryLang,
            subject_id:        subjectId ?? null,
            question_text:     q.question.question_text || '',
            learning_objective: q.question.learning_objective_id || '',
            subject:           subject,
            grade:             grade,
          }),
        }).then(r => r.json())
      )
    ).then(results => {
      results.forEach((r, i) => {
        if (r.status === 'rejected') {
          console.warn('gradeOnServer: question', i, 'failed:', r.reason);
        }
      });
    });
  }

  /* ── handle quiz modal close ── */
  async function handleQuizClose({ correctCount, avgScore, xpEarned, results: qResults }) {
    gradeOnServer(qResults, student_id, sessionId, lang, navState?.subject_id);
    setQuizModalOpen(false);
    setQuizDone(true);
    setQuizResults({ correctCount, avgScore, xpEarned, results: qResults });
    if (correctCount >= 2) {
      consecutiveFailsRef.current = 0;
      setConceptScores(prev => [...prev, {
        lo_index: currentLOIndexRef.current,
        lo_description: learningObjs[currentLOIndexRef.current]?.description_en
          || learningObjs[currentLOIndexRef.current]?.description_ar
          || learningObjs[currentLOIndexRef.current]?.description
          || '',
        topic: topic,
        score: Math.round(avgScore),
      }]);
      setFinalScore(avgScore);
      const xpAmount = correctCount * 10 + (avgScore >= 80 ? 15 : 0);
      setTotalXP(prev => prev + xpAmount);
      const floatId = Date.now();
      setXpFloat({ amount: xpAmount, id: floatId });
      setTimeout(() => setXpFloat(null), 1500);
      setScreenState(STATE.COMPLETE);
      const isLastLO = currentLOIndexRef.current >= learningObjs.length - 1;
      if (isLastLO) {
        try {
          const res = await studentFetch(`${API}/student/session-complete`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body:    JSON.stringify({
              student_id, session_id: sessionId, subject,
              subject_id: navState?.subject_id || null,
              unit,
              score: avgScore,
              topic,
              concepts: [...conceptScores, {
                lo_index: currentLOIndexRef.current,
                lo_description: learningObjs[currentLOIndexRef.current]?.description_en
                  || learningObjs[currentLOIndexRef.current]?.description_ar
                  || learningObjs[currentLOIndexRef.current]?.description
                  || '',
                topic: topic,
                score: Math.round(avgScore),
              }],
              is_unit_complete: false,
              is_final_summary: false,
            }),
          });
          if (!res) return;
          if (!res.ok) {
            console.error(`session-complete failed: HTTP ${res.status}`);
          } else {
            if (student_id) {
              localStorage.removeItem(`jahbath_dashboard_${student_id}`);
              localStorage.removeItem(`jahbath_dashboard_${student_id}_time`);
            }
          }
        } catch (err) {
          console.error('session-complete network error:', err);
        }
      }
    } else {
      if (fastTrackActive) {
        consecutiveFailsRef.current += 1;
        if (consecutiveFailsRef.current >= 2) {
          setFastTrackActive(false);
          consecutiveFailsRef.current = 0;
        }
      }
      const failKey = ++msgKeyRef.current;
      setMessages(prev => [...prev, {
        role: 'ai',
        type: 'text',
        content: lang === 'ar'
          ? `مفيش مشكلة يا ${studentName || 'بطل'}! 💪 قولي إيه اللي مش واضح عندك وأنا هشرهولك بطريقة تانية.`
          : `No worries ${studentName || 'there'}! 💪 Tell me what's unclear and I'll explain it differently.`,
        key: failKey,
      }]);
      setNeedsReExplain(true);
      setScreenState(STATE.HELP);
    }
  }

  /* ── chat bar ── */
  async function sendChat() {
    const messageToSend = chatInput.trim();
    if (!messageToSend || chatSending) return;

    let finalMessage;
    if (awaitingOwnStyle) {
      setAwaitingOwnStyle(false);
      setChatInput('');
      const ownKey = ++msgKeyRef.current;
      setMessages(prev => [...prev, { role: 'user', type: 'text', content: messageToSend, key: ownKey }]);
      startExplain(learningObjs, messageToSend);
      return;
    }
    if (needsReExplain) {
      setNeedsReExplain(false);
      setChatInput('');
      const reKey = ++msgKeyRef.current;
      setMessages(prev => [...prev, { role: 'user', type: 'text', content: messageToSend, key: reKey }]);
      if (reExplainCount >= 2) {
        triggerFoundationReview();
        return;
      }
      setReExplainCount(prev => prev + 1);
      startExplain([learningObjs[currentLOIndexRef.current]], messageToSend);
      return;
    } else {
      finalMessage = messageToSend;
    }

    setChatInput('');
    setChatSending(true);

    const userKey = ++msgKeyRef.current;
    setMessages(prev => [...prev, { role: 'user', type: 'text', content: messageToSend, key: userKey }]);

    let shimmerKey = null;

    try {
      abortRef.current = new AbortController();
      const res = await studentFetch(`${API}/student/chat`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify({ student_id, session_id: sessionId, message: finalMessage, subject, unit, topic, grade, language: lang, student_name: studentName, reexplain_style: null }),
        signal:  abortRef.current.signal,
      });
      if (!res.ok || !res.body) throw new Error('stream failed');

      const reader  = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data:')) continue;
          const raw = line.slice(5).trim();
          if (!raw) continue;
          try {
            const chunk = JSON.parse(raw);
            if (chunk.type === 'text') {
              const key = ++msgKeyRef.current;
              setMessages(prev => {
                const last = prev[prev.length - 1];
                if (last && last.role === 'ai' && last.type === 'text' && last.key !== userKey) {
                  const updated = [...prev];
                  updated[updated.length - 1] = { ...last, content: last.content + chunk.content };
                  return updated;
                }
                return [...prev, { role: 'ai', type: 'text', content: chunk.content, key }];
              });
            } else if (chunk.type === 'image') {
              if (shimmerKey !== null) {
                const keyToRemove = shimmerKey;
                setMessages(prev => prev.filter(m => m.key !== keyToRemove));
                shimmerKey = null;
              }
              const src = chunk.image_url
                ? chunk.image_url
                : `data:${chunk.mime_type || 'image/png'};base64,${chunk.content}`;
              const imgKey = ++msgKeyRef.current;
              setMessages(prev => [...prev, {
                role: 'ai', type: 'chat_image',
                src, alt: chunk.alt || 'صورة توضيحية', key: imgKey,
              }]);
            } else if (chunk.type === 'status') {
              if (chunk.event === 'image_generating' && shimmerKey === null) {
                const newShimmerKey = ++msgKeyRef.current;
                shimmerKey = newShimmerKey;
                setMessages(prev => [...prev, { role: 'ai', type: 'shimmer', key: newShimmerKey }]);
              }
            } else if (chunk.type === 'kp') {
              const kpKey = ++msgKeyRef.current;
              setMessages(prev => [...prev, { role: 'ai', type: 'kp', content: chunk.content, key: kpKey }]);
            } else if (chunk.type === 'error') {
              const errKey = ++msgKeyRef.current;
              setMessages(prev => [...prev, { role: 'ai', type: 'text', content: chunk.message || t.error_chat, key: errKey }]);
            }
          } catch { /* ignore */ }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        const errKey = ++msgKeyRef.current;
        setMessages(prev => [...prev, { role: 'ai', type: 'text', content: t.error_chat, key: errKey }]);
      }
    } finally {
      if (shimmerKey !== null) {
        setMessages(prev => prev.filter(m => m.key !== shimmerKey));
      }
      setChatSending(false);
    }
  }

  function handleChatKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); }
  }

  /* ── retry explain after fail ── */
  function handleRetryExplain() {
    setQuestions([]);

    const autoRetryStyles = grade <= 6
      ? ['everyday life example', 'simple analogy', 'fantasy story']
      : grade <= 9
      ? ['real-world scenario', 'practical application']
      : ['formal exam style — approach from a different angle'];

    const retryStyle = autoRetryStyles[reExplainCount % autoRetryStyles.length];

    startExplain(learningObjs, retryStyle);
  }

  useEffect(() => () => { abortRef.current?.abort(); }, []);

  useEffect(() => {
    if (!lightboxImage) return;
    function onKey(e) { if (e.key === 'Escape') setLightboxImage(null); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxImage]);

  /* ── Owl speech bubble cycling ── */
  useEffect(() => {
    const waitMsgs = lang === 'ar'
      ? ['جارٍ تحضير الشرح...', 'سيظهر الشرح قريباً ✨', 'شكراً على صبرك!']
      : ['Preparing your explanation...', 'Almost ready ✨', 'Thanks for your patience!'];
    if (screenState === STATE.WAITING) {
      let idx = 0;
      setOwlSpeechMsg(waitMsgs[0]);
      const iv = setInterval(() => {
        idx = (idx + 1) % waitMsgs.length;
        setOwlSpeechMsg(waitMsgs[idx]);
      }, 3000);
      return () => clearInterval(iv);
    } else if (screenState === STATE.STREAMING) {
      setOwlSpeechMsg(lang === 'ar' ? 'جهبذ يشرح الآن...' : 'Explaining now...');
    } else if (screenState === STATE.COMPLETE) {
      setOwlSpeechMsg(lang === 'ar' ? 'أحسنت! 🎉' : 'Great job! 🎉');
    } else if (screenState === STATE.CLEAR_Q) {
      setOwlSpeechMsg(lang === 'ar' ? 'هل فهمت؟ 🤔' : 'Did you understand? 🤔');
    } else {
      setOwlSpeechMsg('');
    }
  }, [screenState, lang]); // eslint-disable-line react-hooks/exhaustive-deps


  /* ═══════════════════════════════════════ RENDER ═══════════════════════ */
  return (
    <div className="es-root" dir={dir}>
      <canvas ref={canvasRef} style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }} />
      <style>{CSS}</style>

      {/* ── HEADER (FIX 3 — 52px, logo, divider, subject, XP) ── */}
      <header className="es-header">
        <button
          className="es-logo-btn"
          dir="ltr"
          onClick={() => { abortRef.current?.abort(); navigate('/student/subject-units', { state: { subject_id: navState?.subject_id } }); }}
        >
          <img src="/jahbath-hat.png" alt="Jahbath" style={{ height: 28, display: 'block' }} />
          <span className="es-logo-text">
            <span style={{ color: '#1E3A5F' }}>Jah</span><span style={{ color: '#059669' }}>bath</span>
          </span>
        </button>

        <div className="es-header-divider" />

        <div className="es-header-meta">
          <span className="es-subject-name">{subject}</span>
          <span className="es-unit-lbl">{unit}</span>
        </div>

        {/* Concept Trail */}
        {learningObjs.length > 0 && (
          <div className="es-trail">
            {learningObjs.map((lo, i) => {
              const isDone   = i < currentLOIndex;
              const isActive = i === currentLOIndex;
              return (
                <div key={i} className="es-trail-item">
                  <div
                    className={`es-trail-dot ${isDone ? 'done' : isActive ? 'active' : 'upcoming'}`}
                    title={lo.title || lo.learning_objective || ''}
                  >
                    {isDone ? '✓' : i + 1}
                  </div>
                  {i < learningObjs.length - 1 && (
                    <div className={`es-trail-connector ${isDone ? 'done' : ''}`} />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Stopwatch */}
        <div className="es-stopwatch">
          <div className="es-sw-dot" />
          <span className="es-sw-val">{fmtTime(elapsedSecs)}</span>
        </div>

        {/* XP Badge */}
        <div className="es-xp-badge">⭐ <span className="es-xp-val">{totalXP}</span></div>

        {/* Language toggle */}
        {!isArabicOnlySubject && (
          <button className="es-lang-btn" onClick={() => {
            const next = lang === 'ar' ? 'en' : 'ar';
            setLang(next);
            localStorage.setItem('jahbath_student_lang', next);
            setLang_ctx(next);
          }}>{t.toggle_lang}</button>
        )}
      </header>

      {/* Multi-topic progress strip (Phase A.1 — shows only for queue > 1) */}
      {topicsQueue.length > 1 && (
        <div className="es-topics-progress-strip" dir={dir}>
          <div className="es-tps-label">
            <span className="es-tps-count">
              {t.topic_progress_label(currentTopicIndex + 1, topicsQueue.length)}
            </span>
            <div className="es-tps-bar">
              <div
                className="es-tps-bar-fill"
                style={{ width: `${((currentTopicIndex + 1) / topicsQueue.length) * 100}%` }}
              />
            </div>
          </div>
          <div className="es-tps-pills">
            {topicsQueue.map((tp, i) => {
              const cls = i < currentTopicIndex ? 'done'
                       : i === currentTopicIndex ? 'active'
                       : 'pending';
              return (
                <span key={`tp-${i}`} className={`es-tps-pill ${cls}`} title={tp}>
                  {i < currentTopicIndex ? '✓ ' : ''}{tp}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* ── BODY (two panels — FIX 1 CSS grid, direction drives RTL/LTR placement) ── */}
      <div className="es-panels" style={{ direction: dir }}>

        {/* ── LEFT PANEL — AI explanation stream (FIX 2: glass card + sticky owl) ── */}
        <div className="es-expl-panel" ref={scrollRef}>
          <div className="es-expl-card">

            {/* Concept header (FIX 4) */}
            {learningObjs[currentLOIndex] && (
              <div className="es-concept-header">
                <div className="es-concept-icon">📖</div>
                <span className="es-lo-title">
                  {learningObjs[currentLOIndex]?.title || learningObjs[currentLOIndex]?.learning_objective || ''}
                </span>
              </div>
            )}

            {/* Poem display panel — visible when this unit has a memorization poem */}
            {poemVerses.length > 0 && (
              <div className="es-poem-panel">
                <div
                  className="es-poem-header"
                  onClick={() => setPoemOpen(o => !o)}
                  role="button"
                  aria-expanded={poemOpen}
                >
                  <span className="es-poem-title-text">📜 {navState.poem_title}</span>
                  <span className={`es-poem-chevron${poemOpen ? ' open' : ''}`}>▼</span>
                </div>
                {poemOpen && (
                  <div className="es-poem-verses">
                    {poemVerses.map(v => (
                      v.hemistich2 ? (
                        <div key={v.verse_number} className="es-poem-verse-row">
                          <span className="es-poem-hemistich right">{v.hemistich1}</span>
                          <span className="es-poem-divider" />
                          <span className="es-poem-hemistich left">{v.hemistich2}</span>
                        </div>
                      ) : (
                        <div key={v.verse_number} className="es-poem-verse-full">
                          {v.hemistich1}
                        </div>
                      )
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Content row: text col + sticky owl (FIX 2) */}
            <div className="es-expl-content-row">
              <div className="es-text-col">

                {/* WAITING card (FIX 4 — replaces spinner) */}
                {screenState === STATE.WAITING && !showShimmer && (
                  <div className="es-waiting-card">
                    <div className="es-waiting-dots"><span /><span /><span /></div>
                    <p className="es-wait-text">{t.loading}</p>
                  </div>
                )}

                {/* AI explanation messages */}
                <div className="es-expl-messages">
                  {messages
                    .filter(msg => {
                      // During streaming, exclude the live (last) AI text msg — rendered by streamText below
                      if (screenState === STATE.STREAMING && (msg.key ?? msg.id) === liveStreamMsgKeyRef.current) return false;
                      return (msg.role === 'ai' && (msg.type === 'text' || msg.type === 'image' || msg.type === 'retrying')) || msg.type === 'divider';
                    })
                    .map(msg => {
                      if (msg.role === 'ai' && msg.type === 'text') {
                        return (
                          <div
                            key={msg.key ?? msg.id}
                            className="es-md es-expl-text"
                            dangerouslySetInnerHTML={renderMarkdown(msg.content)}
                          />
                        );
                      }
                      return (
                        <MessageBubble
                          key={msg.key ?? msg.id}
                          msg={msg}
                          dir={dir}
                          t={t}
                          lang={lang}
                          onImageClick={setLightboxImage}
                          onReExplainStyle={handleReExplainStyle}
                        />
                      );
                    })
                  }
                  {/* Live streaming text — rendered directly from streamText state, word by word */}
                  {screenState === STATE.STREAMING && streamText && (
                    <div className="es-md es-expl-text" dangerouslySetInnerHTML={renderMarkdown(streamText)} />
                  )}
                  {/* Shimmer skeleton while waiting for first text chunk (FIX 4) */}
                  {showShimmer && (
                    <div className="es-shimmer-block">
                      <div className="es-sh-line" style={{ width: '80%' }} />
                      <div className="es-sh-line" style={{ width: '92%' }} />
                      <div className="es-sh-line" style={{ width: '68%' }} />
                      <div className="es-sh-line" style={{ width: '85%' }} />
                      <div className="es-sh-line" style={{ width: '75%', marginTop: 14 }} />
                      <div className="es-sh-line" style={{ width: '90%' }} />
                      <div className="es-sh-line" style={{ width: '60%' }} />
                    </div>
                  )}
                </div>
              </div>

              {/* Sticky owl — speech bubble above */}
              <div className="es-owl-teacher">
                <div className="es-owl-inner">
                  {owlSpeechMsg && (
                    <div className={`es-owl-speech${screenState === STATE.STREAMING ? ' es-owl-speech-pulse' : ''}`}>
                      {owlSpeechMsg}
                    </div>
                  )}
                  <img
                    className={`es-owl-img ${owlAnimClass}`}
                    src="/owl.png"
                    alt="جهبذ"
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                </div>
              </div>
            </div>

            {/* Image frame (FIX 3) */}
            {(explImageSrc || isImageLoading) && (
              <div className="es-expl-image-frame">
                {isImageLoading && !explImageSrc
                  ? <div className="es-expl-image-shimmer" />
                  : <img
                      src={`data:image/png;base64,${explImageSrc}`}
                      alt="illustration"
                      style={{ width: '100%', maxHeight: 220, objectFit: 'contain', display: 'block', cursor: 'pointer' }}
                      onClick={() => setLightboxImage({ src: `data:image/png;base64,${explImageSrc}`, alt: 'illustration' })}
                    />
                }
              </div>
            )}

            {/* Key Terms panel (FIX 5) */}
            {keyTerms.length > 0 && (
              <div className="es-key-terms-panel">
                <div className="es-key-terms-header">
                  📌 {lang === 'ar' ? 'المصطلحات' : 'Key Terms'}
                </div>
                <div className="es-key-terms-pills">
                  {keyTerms.map((term, i) => (
                    <span key={i} className="es-key-term-pill">{term}</span>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>

        {/* ── RIGHT PANEL — interaction (FIX 5: chat header + glass) ── */}
        <div className="es-chat-panel">

          {/* Chat header (FIX 5) */}
          <div className="es-chat-header">
            <div className="es-chat-avatar-box">{dir === 'ar' ? 'ج' : 'J'}</div>
            <div className="es-chat-info">
              <div className="es-chat-name">{dir === 'ar' ? 'جهبذ' : 'Jahbath'}</div>
              <div className="es-chat-status">
                <div className="es-status-dot" />
                {dir === 'ar' ? 'متصل' : 'Online'}
              </div>
            </div>
            <div className="es-chat-hint-pill">
              {dir === 'ar' ? 'اسألني أي سؤال' : 'Ask me anything'}
            </div>
          </div>

          {/* Scrollable message area */}
          <div
            ref={chatScrollRef}
            className="es-chat"
            style={{
              direction: lang === 'ar' ? 'rtl' : 'ltr',
              textAlign: lang === 'ar' ? 'right' : 'left',
              fontFamily: lang === 'ar' ? "'Noto Kufi Arabic', sans-serif" : 'inherit',
            }}
          >
            {/* ERROR */}
            {screenState === STATE.ERROR && (
              <div className="es-center-state">
                <p className="es-error-text">{errorMsg}</p>
                <button className="es-retry-btn" onClick={() => {
                  setErrorMsg(''); setScreenState(STATE.WAITING); startExplain(learningObjs);
                }}>{t.retry}</button>
              </div>
            )}

            {/* Chat/interaction messages (everything not in left panel) */}
            {messages
              .filter(msg => !((msg.role === 'ai' && (msg.type === 'text' || msg.type === 'image' || msg.type === 'retrying')) || msg.type === 'divider'))
              .map(msg => (
                <MessageBubble
                  key={msg.key ?? msg.id}
                  msg={msg}
                  dir={dir}
                  t={t}
                  lang={lang}
                  onImageClick={setLightboxImage}
                  onReExplainStyle={handleReExplainStyle}
                />
              ))
            }

            {/* Thinking dots while streaming */}
            {screenState === STATE.STREAMING && messages.length === 0 && (
              <div className="es-bubble-row es-ai-row">
                <AiAvatar lang={lang} />
                <div className="es-bubble es-bubble-ai es-thinking"><span /><span /><span /></div>
              </div>
            )}

            {/* CLEAR_Q — comprehension check */}
            {screenState === STATE.CLEAR_Q && (
              <div className="es-clear-q">
                <p className="es-clear-label">{t.ask_understood}</p>
                <div className="es-clear-btns">
                  <button className="es-btn-yes" onClick={handleYesClear}>{t.yes_clear}</button>
                  <button className="es-btn-no"  onClick={handleNoHelp}>{t.no_help}</button>
                </div>
              </div>
            )}

            {/* HELP — understood button */}
            {screenState === STATE.HELP && (
              <div className="es-understood-wrap">
                <button className="es-understood-btn" onClick={() => setScreenState(STATE.CLEAR_Q)}>
                  {t.understood_now}
                </button>
              </div>
            )}

            {/* FAIL */}
            {screenState === STATE.FAIL && (
              <div className="es-fail-wrap">
                <button className="es-retry-explain-btn" onClick={handleRetryExplain}>
                  {t.retry_explain}
                </button>
              </div>
            )}

            {/* COMPLETE */}
            {screenState === STATE.COMPLETE && (
              <div className="es-complete-panel">
                <div className="es-complete-icon">🌟</div>
                <div className="es-complete-score">
                  <span className="es-score-num" style={{ color: finalScore >= 60 ? '#059669' : '#d97706' }}>{Math.round(finalScore)}%</span>
                  <span className="es-score-label">{lang === 'ar' ? 'نتيجتك' : 'Your Score'}</span>
                </div>
                <p className="es-complete-text">{t.session_complete}</p>
                <div className="es-complete-btns">
                  <button
                    className="es-next-btn"
                    disabled={isCompleting}
                    style={isCompleting ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                    onClick={() => {
                      // Phase A.2: continue to next topic (multi-topic flow)
                      if (currentLOIndexRef.current >= learningObjs.length - 1 &&
                          currentTopicIndexRef.current < topicsQueue.length - 1) {
                        if (!isCompleting) {
                          setIsCompleting(true);
                          advanceToNextTopic().finally(() => setIsCompleting(false));
                        }
                        return;
                      }
                      const isLastLO = currentLOIndexRef.current >= learningObjs.length - 1;
                      if (!isLastLO) {
                        const nextIndex = currentLOIndexRef.current + 1;
                        currentLOIndexRef.current = nextIndex;
                        setCurrentLOIndex(nextIndex);
                        const dividerKey = ++msgKeyRef.current;
                        const nextLO = learningObjs[nextIndex];
                        const nextLODesc = lang === 'ar'
                          ? (nextLO?.description_ar || nextLO?.description || nextLO?.description_en || nextLO?.id || '')
                          : (nextLO?.description_en || nextLO?.description || nextLO?.description_ar || nextLO?.id || '');
                        setMessages(prev => [...prev,
                          { role: 'divider', type: 'divider', topic: nextLODesc, id: `divider-lo-${nextIndex}`, key: dividerKey }
                        ]);
                        setScreenState(STATE.WAITING);
                        setQuizDone(false);
                        setQuizResults(null);
                        startExplain([learningObjs[nextIndex]]);
                      } else {
                        const hasMoreTopics = currentTopicIndexRef.current < topicsQueue.length - 1;
                        if (hasMoreTopics) {
                          setTopicResults(prev => [...prev, {
                            topic: topicsQueue[currentTopicIndexRef.current] || topic,
                            score: Math.round(finalScore || 0),
                            xp: totalXP,
                            completed_at: Date.now(),
                          }]);
                          setScreenState(STATE.COMPLETE);
                          return;
                        }
                        const checkUnitComplete = async () => {
                          let isUnitComplete = false;
                          try {
                            const unitId = navState?.unit_id || null;
                            if (unitId && topicList.length > 0) {
                              const res = await studentFetch(`${API}/student/completed-topics/${student_id}/${unitId}`, {
                                headers: { Authorization: `Bearer ${token}` },
                              });
                              if (!res) return;
                              const data = await res.json();
                              const completed = data.completed_topics || [];
                              const allDone = topicList.every(t => t === topic || completed.includes(t));
                              isUnitComplete = allDone;
                            }
                          } catch { isUnitComplete = false; }

                          if (isUnitComplete) {
                            try {
                              const res = await studentFetch(`${API}/student/session-complete`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                body: JSON.stringify({
                                  student_id, session_id: sessionId, subject,
                                  subject_id: navState?.subject_id || null,
                                  unit,
                                  score: finalScore,
                                  concepts: conceptScores,
                                  is_unit_complete: true,
                                  is_final_summary: true,
                                }),
                              });
                              if (!res) return;
                              if (!res.ok) {
                                console.error(`session-complete failed: HTTP ${res.status}`);
                              } else {
                                if (student_id) {
                                  localStorage.removeItem(`jahbath_dashboard_${student_id}`);
                                  localStorage.removeItem(`jahbath_dashboard_${student_id}_time`);
                                }
                              }
                            } catch (err) {
                              console.error('session-complete network error:', err);
                            }
                          }

                          navigate('/student/subject-units', { state: { subject_id: navState?.subject_id } });
                        };
                        if (!isCompleting) {
                          setIsCompleting(true);
                          checkUnitComplete().finally(() => setIsCompleting(false));
                        }
                      }
                    }}
                  >
                    {currentLOIndexRef.current < learningObjs.length - 1
                      ? (lang === 'ar' ? '← الفكرة التالية' : 'Next Concept →')
                      : (currentTopicIndexRef.current < topicsQueue.length - 1
                          ? t.continue_to_next
                          : (lang === 'ar' ? 'إكمال الموضوع' : 'Complete Topic'))}
                  </button>
                  {currentLOIndexRef.current >= learningObjs.length - 1 &&
                   currentTopicIndexRef.current < topicsQueue.length - 1 && (
                    <button
                      className="es-back-btn-secondary"
                      style={{ marginLeft: 8 }}
                      onClick={() => setScreenState(STATE.FINAL_SUMMARY)}
                    >
                      {t.finish_session_btn}
                    </button>
                  )}
                  <button
                    className="es-back-btn-secondary"
                    onClick={() => navigate('/student/subject-units', { state: { subject_id: navState?.subject_id } })}
                  >
                    {t.back_subjects}
                  </button>
                </div>
              </div>
            )}
            {/* FINAL_SUMMARY — multi-topic session complete */}
            {screenState === STATE.FINAL_SUMMARY && (
              <div className="es-final-panel">
                <div className="es-final-icon">🏆</div>
                <div className="es-final-title">{t.session_complete_title}</div>
                <div className="es-final-stats">
                  <div className="es-final-stat">
                    <div className="es-final-stat-value">{topicResults.length}</div>
                    <div className="es-final-stat-label">{t.stat_topics}</div>
                  </div>
                  <div className="es-final-stat">
                    <div className="es-final-stat-value">{totalXP}</div>
                    <div className="es-final-stat-label">{t.stat_xp}</div>
                  </div>
                  <div className="es-final-stat">
                    <div className="es-final-stat-value">
                      {topicResults.length > 0
                        ? Math.round(topicResults.reduce((s, r) => s + (r.score || 0), 0) / topicResults.length)
                        : 0}%
                    </div>
                    <div className="es-final-stat-label">{t.stat_avg_score}</div>
                  </div>
                </div>
                <button
                  className="es-next-btn"
                  onClick={() => {
                    try {
                      studentFetch(`${API}/student/session-complete`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({
                          student_id, session_id: sessionId, subject,
                          subject_id: navState?.subject_id || null,
                          unit,
                          score: topicResults.length > 0
                            ? Math.round(topicResults.reduce((s, r) => s + (r.score || 0), 0) / topicResults.length)
                            : 0,
                          concepts: conceptScores,
                          is_unit_complete: false,
                          is_final_summary: true,
                        }),
                      }).catch(() => {});
                      if (student_id) {
                        localStorage.removeItem(`jahbath_dashboard_${student_id}`);
                        localStorage.removeItem(`jahbath_dashboard_${student_id}_time`);
                      }
                    } catch {}
                    navigate('/student/subject-units', { state: { subject_id: navState?.subject_id } });
                  }}
                >
                  ← {t.back_to_units}
                </button>
              </div>
            )}
          </div>{/* end es-chat */}

          {/* Sticky chat input bar */}
          {showChatBar && (
            <div className="es-chat-bar">
              <textarea
                ref={chatInputRef}
                className="es-chat-textarea"
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={handleChatKeyDown}
                placeholder={t.chat_ph}
                disabled={chatSending}
                dir={dir}
                rows={1}
              />
              <button
                className="es-chat-send"
                onClick={sendChat}
                disabled={chatSending || !chatInput.trim()}
                aria-label={t.send}
              >
                {chatSending ? '…' : '↑'}
              </button>
            </div>
          )}
        </div>{/* end es-chat-panel */}
      </div>{/* end es-panels */}

      {/* XP float (FIX 5) */}
      {xpFloat && (
        <div className="es-xp-float" key={xpFloat.id}>+{xpFloat.amount} XP ⭐</div>
      )}

      {/* Lightbox */}
      {lightboxImage && (
        <div className="es-lightbox" onClick={() => setLightboxImage(null)}>
          <button className="es-lightbox-close" onClick={() => setLightboxImage(null)}>✕</button>
          <div className="es-lightbox-inner" onClick={e => e.stopPropagation()}>
            <img className="es-lightbox-img" src={lightboxImage.src} alt={lightboxImage.alt} />
            {lightboxImage.alt && (
              <p className="es-lightbox-caption">{lightboxImage.alt}</p>
            )}
          </div>
        </div>
      )}

      {/* Quiz Modal */}
      {quizModalOpen && (
        <QuizModal
          questions={quizQuestions}
          lang={lang}
          topic={topic}
          onClose={handleQuizClose}
        />
      )}
    </div>
  );
}

/* ─── QuizModal ──────────────────────────────────────────────────────────── */
const MODAL_OVERLAY = {
  position: 'fixed', inset: 0, zIndex: 1000,
  background: 'rgba(0,0,0,0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '16px',
};
const MODAL_CARD = {
  background: '#fff', borderRadius: 24,
  padding: '28px 24px', width: '100%', maxWidth: 560,
  maxHeight: '90vh', overflowY: 'auto',
  border: '2px solid #E5E7EB',
  boxShadow: '0 8px 0 #E5E7EB, 0 24px 60px rgba(0,0,0,0.18)',
};
const AR_LETTERS_M = { A: 'أ', B: 'ب', C: 'ج', D: 'د' };

function QuizModal({ questions, lang, topic, onClose }) {
  const isAr = lang === 'ar';
  const [cur,          setCur]          = useState(0);
  const [selected,     setSelected]     = useState(null);
  const [answered,     setAnswered]     = useState(false);
  const [localResults, setLocalResults] = useState([]);
  const [phase,        setPhase]        = useState('quiz');

  function handlePick(opt) {
    if (answered) return;
    setSelected(opt);
    setAnswered(true);
    const q = questions[cur];
    const waeQ = q.wrong_answer_explanations || {};
    const optLetter = opt.split('.')[0].trim().toUpperCase();
    const correctLetter = (q.correct_answer || '').trim()[0]?.toUpperCase();
    const isCorrect = correctLetter
        ? optLetter === correctLetter
        : !(optLetter in waeQ);
    const newResults = [...localResults, { question: q, answer: opt, isCorrect }];
    setLocalResults(newResults);
    setTimeout(() => {
      if (cur + 1 >= questions.length) {
        setPhase('results');
      } else {
        setCur(c => c + 1);
        setSelected(null);
        setAnswered(false);
      }
    }, 500);
  }

  /* ── Results view ── */
  if (phase === 'results') {
    const correctCount = localResults.filter(r => r.isCorrect).length;
    const avgScore     = localResults.length > 0
      ? Math.round((correctCount / localResults.length) * 100) : 0;
    const xpEarned     = correctCount * 10 + (avgScore >= 80 ? 15 : 0);
    const scoreColor   = avgScore >= 80 ? '#059669' : avgScore >= 60 ? '#D97706' : '#DC2626';
    const wrongAnswers = localResults.filter(r => !r.isCorrect);

    return (
      <div style={MODAL_OVERLAY}>
        <div style={MODAL_CARD} dir={isAr ? 'rtl' : 'ltr'}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ fontSize: 52, marginBottom: 6 }}>
              {avgScore >= 80 ? '🏆' : avgScore >= 60 ? '🌟' : '💪'}
            </div>
            <div style={{ fontSize: 52, fontWeight: 800, color: scoreColor, lineHeight: 1 }}>{avgScore}%</div>
            <div style={{ fontSize: 13, color: '#6B7280', marginTop: 6 }}>
              {isAr
                ? `${correctCount} من ${localResults.length} إجابات صحيحة`
                : `${correctCount} of ${localResults.length} correct`}
            </div>
          </div>
          <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <span style={{ fontSize: 18 }}>⭐</span>
            <span style={{ fontWeight: 800, color: '#92400E', fontSize: 16 }}>+{xpEarned} XP</span>
            <span style={{ fontSize: 12, color: '#B45309' }}>{isAr ? 'نقاط مكتسبة' : 'earned'}</span>
          </div>
          {wrongAnswers.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#374151', marginBottom: 10 }}>
                {isAr ? 'مراجعة الأخطاء' : 'Review'}
              </div>
              {wrongAnswers.map(({ question, answer }, i) => {
                const wae = question.wrong_answer_explanations || {};
                const ansLetter = answer?.split('.')[0]?.trim();
                const explanation = wae[ansLetter] || '';
                const correctOpt = (question.answer_options || []).find(o => !(o.split('.')[0].trim() in wae)) || '';
                return (
                  <div key={i} style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 10, padding: '12px 14px', marginBottom: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#991B1B', marginBottom: 4 }}>{question.question_text}</div>
                    <div style={{ fontSize: 12, color: '#B91C1C' }}>{isAr ? `إجابتك: ${answer}` : `Your answer: ${answer}`}</div>
                    <div style={{ fontSize: 12, color: '#065F46', marginTop: 4 }}>{isAr ? `الصحيح: ${correctOpt}` : `Correct: ${correctOpt}`}</div>
                    {explanation && (
                      <div style={{ fontSize: 12, color: '#374151', marginTop: 4, fontStyle: 'italic' }}>{explanation}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <button
            style={{ width: '100%', background: 'linear-gradient(135deg,#059669,#047857)', color: '#fff', border: 'none', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}
            onClick={() => onClose({ correctCount, avgScore, xpEarned, results: localResults })}
          >
            {correctCount >= 2
              ? (isAr ? 'أحسنت! متابعة ←' : 'Well done! Continue →')
              : (isAr ? 'إعادة الشرح' : 'Review Explanation')}
          </button>
        </div>
      </div>
    );
  }

  /* ── Question view ── */
  const q    = questions[cur];
  const opts = q.answer_options || [];

  return (
    <div style={MODAL_OVERLAY}>
      <div style={MODAL_CARD} dir={isAr ? 'rtl' : 'ltr'}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#059669' }}>
            {isAr ? `السؤال ${cur + 1} من ${questions.length}` : `Question ${cur + 1} of ${questions.length}`}
          </span>
          <span style={{ fontSize: 12, color: '#9CA3AF' }}>{topic}</span>
        </div>
        <div style={{ height: 6, background: '#E5E7EB', borderRadius: 99, marginBottom: 20, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${((cur + (answered ? 1 : 0)) / questions.length) * 100}%`, background: 'linear-gradient(90deg,#059669,#10B981)', borderRadius: 99, transition: 'width 0.4s ease' }} />
        </div>
        <p style={{ fontSize: 16, fontWeight: 600, color: '#111827', lineHeight: 1.65, marginBottom: 20, direction: isAr ? 'rtl' : 'ltr' }}>
          {q.question_text}
        </p>
        {opts.map((opt, i) => {
          const letter        = opt.split('.')[0].trim().toUpperCase();
          const text          = opt.split('. ').slice(1).join('. ');
          const isThisOpt     = selected === opt;
          const waeQ          = q.wrong_answer_explanations || {};
          const correctLetter = (q.correct_answer || '').trim()[0]?.toUpperCase();
          const isCorrectOpt  = correctLetter ? letter === correctLetter : !(letter in waeQ);
          let bg = '#F9FAFB', border = '1.5px solid #E5E7EB', txtColor = '#1F2937';
          if (answered) {
            if (isCorrectOpt)   { bg = '#D1FAE5'; border = '1.5px solid #059669'; txtColor = '#065F46'; }
            else if (isThisOpt) { bg = '#FEE2E2'; border = '1.5px solid #DC2626'; txtColor = '#7F1D1D'; }
            else                { txtColor = '#9CA3AF'; }
          }
          const circleBg    = answered && isCorrectOpt ? '#059669' : answered && isThisOpt ? '#DC2626' : '#E5E7EB';
          const circleColor = answered && (isCorrectOpt || isThisOpt) ? '#fff' : '#374151';
          return (
            <div key={i} onClick={() => handlePick(opt)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderRadius: 12, marginBottom: 8, cursor: answered ? 'default' : 'pointer', background: bg, border, color: txtColor, transition: 'all 0.2s' }}>
              <div style={{ width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: circleBg, color: circleColor, fontWeight: 700, fontSize: 13 }}>
                {isAr ? (AR_LETTERS_M[letter] || letter) : letter}
              </div>
              <span style={{ fontSize: 14, flex: 1 }}>{text}</span>
              {answered && isCorrectOpt && <span>✓</span>}
              {answered && isThisOpt && !isCorrectOpt && <span>✗</span>}
            </div>
          );
        })}
        {answered && selected && (() => {
          const selLetter  = selected?.split('.')[0]?.trim();
          const selCorrect = !(selLetter in (q.wrong_answer_explanations || {}));
          return (
            <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 10, background: selCorrect ? '#D1FAE5' : '#FEE2E2', fontSize: 13, color: selCorrect ? '#065F46' : '#7F1D1D', fontWeight: 600 }}>
              {selCorrect
                ? (isAr ? '✓ إجابة صحيحة! 🎉' : '✓ Correct! 🎉')
                : (isAr ? '✗ إجابة خاطئة' : '✗ Wrong answer')}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

/* ─── MessageBubble ──────────────────────────────────────────────────────── */
function MessageBubble({ msg, dir, t, lang, onImageClick, onReExplainStyle }) {
  const detectDir = (text) => {
    const match = (text || '').match(/[\u0600-\u06FF\u0041-\u007A]/);
    if (!match) return dir;
    return /[\u0600-\u06FF]/.test(match[0]) ? 'rtl' : 'ltr';
  };

  if (msg.type === 'divider') {
    return (
      <div key={msg.id} role="separator" style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '24px 0 8px', padding: '0 4px' }}>
        <div style={{ flex: 1, height: '1px', background: '#E5E7EB' }} />
        <span style={{ fontSize: '11px', color: '#9CA3AF', whiteSpace: 'nowrap' }}>
          {lang === 'ar' ? 'المفهوم التالي ↓' : 'Next Concept ↓'}
        </span>
        <div style={{ flex: 1, height: '1px', background: '#E5E7EB' }} />
      </div>
    );
  }

  if (msg.type === 'quiz_loading') {
    return (
      <div className="es-bubble-row es-ai-row">
        <AiAvatar lang={lang} />
        <div className="es-bubble es-bubble-ai es-thinking"><span /><span /><span /></div>
      </div>
    );
  }

  if (msg.type === 'shimmer') {
    return (
      <div className="es-bubble-row es-ai-row">
        <AiAvatar lang={lang} />
        <div className="es-bubble es-bubble-ai">
          <div className="es-shimmer-bubble" />
          <p style={{ fontSize: 12, color: '#059669', marginTop: 8, textAlign: 'center', fontFamily: 'Nunito' }}>
            {lang === 'ar' ? '🎨 جاري رسم الصورة...' : '🎨 Generating illustration...'}
          </p>
        </div>
      </div>
    );
  }

  if (msg.type === 'retrying') {
    return (
      <div key={msg.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', color: '#6B7280', fontSize: 13, fontStyle: 'italic' }}>
        <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⟳</span>
        {lang === 'ar' ? '…جاري إعادة المحاولة' : 'Retrying…'}
      </div>
    );
  }

  if (msg.type === 'chat_image') {
    return (
      <div className="es-bubble-row es-ai-row">
        <AiAvatar lang={lang} />
        <div className="es-bubble es-bubble-ai">
          <img src={msg.src} alt={msg.alt} className="es-img-thumb" onClick={() => onImageClick({ src: msg.src, alt: msg.alt })} />
          <p className="es-img-caption">{t.img_enlarge}</p>
        </div>
      </div>
    );
  }

  if (msg.type === 'fail') {
    return (
      <div className="es-bubble-row es-ai-row">
        <AiAvatar lang={lang} />
        <div className="es-bubble es-bubble-ai es-fail-bubble">{msg.content}</div>
      </div>
    );
  }

  if (msg.type === 'reexplain_prompt') {
    return (
      <div className="es-bubble-row es-ai-row">
        <AiAvatar lang={lang} />
        <div className="es-bubble es-bubble-ai" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <p style={{ margin: 0, fontSize: 14 }}>
            {lang === 'ar' ? 'كيف تحب أن أعيد الشرح؟' : 'How would you like me to re-explain?'}
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
            {msg.styleOptions.map((style, i) => (
              <button
                key={i}
                onClick={() => onReExplainStyle(style)}
                style={{ background: 'linear-gradient(135deg,#059669,#10B981)', color: 'white', border: 'none', borderRadius: 20, padding: '6px 14px', fontSize: 13, cursor: 'pointer', fontFamily: 'Nunito' }}
              >
                {style}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (msg.type === 'kp') {
    return (
      <div className="es-bubble-row es-ai-row">
        <AiAvatar lang={lang} />
        <div className="es-bubble es-bubble-ai es-kp-bubble">
          <div className="es-kp-label">📌 {lang === 'ar' ? 'نقطة أساسية' : 'Key Point'}</div>
          <div className="es-md" dangerouslySetInnerHTML={renderMarkdown(msg.content)} />
        </div>
      </div>
    );
  }

  if (msg.type === 'system') {
    return <div className="es-sys-msg">{msg.content}</div>;
  }

  if (msg.type === 'text') {
    return (
      <div className={`es-bubble-row ${msg.role === 'user' ? 'es-user-row' : (detectDir(msg.content) === 'rtl' ? 'es-ai-row-rtl' : 'es-ai-row')}`} style={msg.role === 'ai' ? { alignItems: 'flex-start' } : {}}>
        {msg.role === 'ai' && <AiAvatar lang={lang} />}
        {msg.role === 'ai' ? (
          <div className="es-bubble es-bubble-ai" dir={detectDir(msg.content)}>
            <div className="es-md" dangerouslySetInnerHTML={renderMarkdown(msg.content)} />
            {msg.imageFailed && (
              <p style={{ margin: '8px 0 0', fontSize: 12, color: '#9CA3AF', fontStyle: 'italic', fontFamily: 'Nunito' }}>
                {lang === 'ar' ? '(تعذّر تحميل الصورة)' : '(illustration unavailable)'}
              </p>
            )}
            {msg.imagecoming && !msg.imageSrc && !msg.imageFailed && (
              <div className="es-img-inline-shimmer" />
            )}
            {msg.imageSrc && (
              <div className="es-img-inline">
                <img
                  src={`data:image/png;base64,${msg.imageSrc}`}
                  alt="illustration"
                  style={{ width: '100%', maxHeight: 220, objectFit: 'contain', cursor: 'pointer', display: 'block' }}
                  onClick={() => onImageClick({ src: `data:image/png;base64,${msg.imageSrc}`, alt: 'illustration' })}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="es-bubble es-bubble-user">
            <span style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</span>
          </div>
        )}
      </div>
    );
  }

  const bubbleDir = msg.type === 'image' ? 'ltr' : detectDir(msg.content);

  return (
    <div className={`es-bubble-row ${msg.role === 'user' ? 'es-user-row' : (bubbleDir === 'rtl' ? 'es-ai-row-rtl' : 'es-ai-row')}`}>
      {msg.role === 'ai' && <AiAvatar lang={lang} />}
      <div className={`es-bubble ${msg.role === 'ai' ? 'es-bubble-ai' : 'es-bubble-user'}`}>
        {msg.type === 'image'
          ? <>
              <div className="es-img-wrapper es-img-standalone">
                <img
                  src={`data:image/png;base64,${msg.content}`}
                  alt="explanation"
                  className="es-msg-img"
                  onClick={() => onImageClick({ src: `data:image/png;base64,${msg.content}`, alt: t.img_enlarge })}
                />
                <p className="es-img-caption">{t.img_enlarge}</p>
              </div>
            </>
          : msg.role === 'ai'
            ? <div className="es-md" dangerouslySetInnerHTML={renderMarkdown(msg.content)} />
            : <span style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</span>
        }
      </div>
    </div>
  );
}

function AiAvatar({ lang }) {
  return (
    <div className="es-avatar">
      <div className="es-avatar-box">{lang === 'ar' ? 'ج' : 'J'}</div>
    </div>
  );
}

/* ─── CSS ────────────────────────────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=Nunito:wght@400;500;600;700;800&display=swap');

html, body { overflow: hidden; height: 100%; margin: 0; padding: 0; }

.es-root {
  display: flex; flex-direction: column;
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  height: 100vh; max-height: 100vh; overflow: hidden;
  background: #edfdf5; font-family: 'Nunito', sans-serif;
}

/* ── Header (FIX 3 — 52px, logo, divider, subject, XP badge) ── */
.es-header {
  display: flex; align-items: center; gap: 10px; padding: 0 16px;
  background: rgba(237,253,245,.92);
  backdrop-filter: blur(32px) saturate(2.2); -webkit-backdrop-filter: blur(32px) saturate(2.2);
  border-bottom: 1px solid rgba(5,150,105,.12); flex-shrink: 0; overflow: hidden;
  height: 52px; min-height: 52px; max-height: 52px; box-sizing: border-box;
  position: relative; z-index: 10;
}
.es-logo-btn {
  display: flex; align-items: center; gap: 6px;
  background: transparent; border: none; cursor: pointer;
  padding: 4px 8px 4px 0; flex-shrink: 0;
}
.es-logo-btn:hover, .es-logo-btn:focus { background: transparent; outline: none; }
.es-logo-text { font-family: 'Sora',sans-serif; font-size: 18px; font-weight: 700; display: flex; align-items: center; white-space: nowrap; }
.es-header-divider { width: 1px; height: 20px; background: rgba(5,150,105,.2); flex-shrink: 0; }
.es-header-meta { display: flex; flex-direction: column; gap: 1px; min-width: 0; flex-shrink: 0; }
.es-subject-name {
  font-size: 13px; font-weight: 700; color: #065F46; font-family: 'Sora',sans-serif;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 140px;
}
.es-unit-lbl {
  font-size: 10px; color: #6b7280;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 140px;
}

.es-trail { display: flex; align-items: center; gap: 0; overflow-x: auto; padding: 0 4px; flex: 1; }
.es-trail::-webkit-scrollbar { display: none; }
.es-trail-item { display: flex; align-items: center; gap: 0; flex-shrink: 0; }
.es-trail-dot {
  width: 26px; height: 26px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-family: 'JetBrains Mono','Courier New',monospace; font-size: 10px; font-weight: 800;
  transition: all .3s cubic-bezier(.34,1.56,.64,1); flex-shrink: 0;
}
.es-trail-dot.done  { background: linear-gradient(135deg,#059669,#10B981); color:#fff; box-shadow:0 3px 10px rgba(5,150,105,.3); }
.es-trail-dot.active { background: linear-gradient(135deg,#D97706,#F59E0B); color:#fff; box-shadow:0 3px 10px rgba(217,119,6,.3); animation: es-trail-pulse 2s ease-in-out infinite; }
.es-trail-dot.upcoming { background: rgba(0,0,0,.07); color: #D1D5DB; }
@keyframes es-trail-pulse {
  0%,100%{box-shadow:0 3px 10px rgba(217,119,6,.3)}
  50%{box-shadow:0 3px 18px rgba(217,119,6,.55),0 0 0 4px rgba(217,119,6,.1)}
}
.es-trail-connector { width: 14px; height: 2px; background: rgba(0,0,0,.1); flex-shrink: 0; }
.es-trail-connector.done { background: linear-gradient(90deg,#059669,#10B981); }

.es-stopwatch {
  display: flex; align-items: center; gap: 6px;
  background: rgba(255,255,255,.8); backdrop-filter: blur(10px);
  border: 1px solid rgba(0,0,0,.08); border-radius: 100px; padding: 4px 10px; flex-shrink: 0;
}
.es-sw-dot { width: 12px; height: 12px; border-radius: 50%; border: 2px solid #059669; position: relative; flex-shrink: 0; }
.es-sw-dot::after {
  content: ''; position: absolute; top: 1px; right: 50%;
  width: 1.5px; height: 3px; background: #059669;
  transform: translateX(50%); border-radius: 2px;
  transform-origin: bottom center; animation: es-tick 1s steps(60) infinite;
}
@keyframes es-tick { to { transform: translateX(50%) rotate(360deg); } }
.es-sw-val { font-size: 11px; font-weight: 600; color: #1E3A5F; min-width: 34px; }

/* XP badge (FIX 3) */
.es-xp-badge {
  display: flex; align-items: center; gap: 4px;
  background: rgba(254,243,199,.9); border: 1px solid rgba(253,211,77,.5);
  border-radius: 100px; padding: 3px 10px;
  font-size: 11px; font-weight: 700; color: #92400E; flex-shrink: 0; white-space: nowrap;
}
.es-xp-val { font-size: 12px; font-weight: 800; }

.es-lang-btn {
  background: rgba(5,150,105,.1); border: 1px solid rgba(5,150,105,.2);
  color: #059669; font-size: 12px; font-weight: 700;
  padding: 4px 10px; border-radius: 20px; cursor: pointer;
  transition: all .15s; white-space: nowrap; flex-shrink: 0;
}
.es-lang-btn:hover { background: rgba(5,150,105,.18); }

/* ── Two-panel body (FIX 1 — CSS Grid, direction prop drives RTL/LTR) ── */
.es-panels {
  display: grid; grid-template-columns: 1fr 340px;
  flex: 1; overflow: hidden; min-height: 0;
  height: calc(100vh - 52px); width: 100%;
}

/* ── Left panel (FIX 2: glass card, sticky owl) ── */
.es-expl-panel {
  min-width: 0; overflow-y: auto; padding: 20px 20px 24px;
  display: flex; flex-direction: column; gap: 16px;
  height: 100%; max-height: 100%; background: transparent;
  position: relative; box-sizing: border-box;
}
.es-expl-panel::-webkit-scrollbar { width: 4px; }
.es-expl-panel::-webkit-scrollbar-thumb { background: rgba(0,0,0,.1); border-radius: 4px; }

/* Glass explanation card (FIX 4 — auto-expand, scroll parent handles overflow) */
.es-expl-card {
  background: rgba(255,255,255,.72);
  backdrop-filter: blur(28px) saturate(2); -webkit-backdrop-filter: blur(28px) saturate(2);
  border-radius: 20px; border: 1px solid rgba(255,255,255,.9);
  box-shadow: 0 8px 32px rgba(5,150,105,.08);
  padding: 20px 20px 16px; height: auto; min-height: 200px; flex-shrink: 0; display: flex; flex-direction: column; gap: 14px;
}

/* Concept header (FIX 4) */
.es-concept-header {
  display: flex; align-items: center; gap: 10px;
  padding-bottom: 12px; border-bottom: 1px solid rgba(5,150,105,.1); flex-shrink: 0;
}
.es-concept-icon {
  width: 32px; height: 32px; border-radius: 10px;
  background: linear-gradient(135deg,rgba(5,150,105,.12),rgba(16,185,129,.08));
  border: 1px solid rgba(5,150,105,.15);
  display: flex; align-items: center; justify-content: center; font-size: 16px; flex-shrink: 0;
}
.es-lo-title { font-family: 'Sora',sans-serif; font-size: 14px; font-weight: 700; color: #1E3A5F; line-height: 1.4; }

/* Content row: text + sticky owl (FIX 2) */
.es-expl-content-row { display: flex; gap: 16px; flex: 1; align-items: flex-start; }
.es-text-col { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 12px; }

/* Sticky owl 170px with speech bubble */
.es-owl-teacher {
  flex-shrink: 0; width: 170px; position: sticky; top: 20px;
  align-self: flex-start; display: flex; align-items: flex-end; justify-content: center;
  overflow: visible;
}
.es-owl-inner {
  position: relative; display: flex; flex-direction: column; align-items: center;
}
.es-owl-speech {
  position: absolute; bottom: calc(100% + 8px); left: 50%; transform: translateX(-50%);
  background: white; border: 1px solid #D1FAE5; border-radius: 12px;
  padding: 6px 12px; font-size: 12px; color: #059669;
  font-family: 'Nunito',sans-serif; white-space: nowrap;
  box-shadow: 0 2px 8px rgba(5,150,105,.12); z-index: 5;
}
.es-owl-speech::after {
  content: ''; position: absolute; top: 100%; left: 50%; transform: translateX(-50%);
  border: 6px solid transparent; border-top-color: #D1FAE5;
}
.es-owl-speech-pulse { animation: es-pill-pulse 1.5s ease-in-out infinite; }
.es-owl-img { width: 170px; height: auto; display: block; filter: drop-shadow(0 8px 20px rgba(5,150,105,.25)); }

/* 4-state owl animations (FIX 2) */
.es-owl-teach  { animation: owlTeach  5s ease-in-out infinite; }
.es-owl-think  { animation: owlThink  2s ease-in-out infinite; }
.es-owl-celeb  { animation: owlCeleb  0.6s ease-in-out infinite alternate; }
.es-owl-idle   { animation: owlIdle   4s ease-in-out infinite; }
.es-owl-flipped.es-owl-teach { animation: owlTeachF 5s ease-in-out infinite; }
.es-owl-flipped.es-owl-think { animation: owlThinkF 2s ease-in-out infinite; }
.es-owl-flipped.es-owl-celeb { animation: owlCelebF 0.6s ease-in-out infinite alternate; }
.es-owl-flipped.es-owl-idle  { animation: owlIdleF  4s ease-in-out infinite; }
@keyframes owlTeach  { 0%,100%{transform:translateY(0) rotate(-2deg) scaleX(1)}  50%{transform:translateY(-8px) rotate(2deg) scaleX(1)} }
@keyframes owlThink  { 0%,100%{transform:translateY(0) rotate(-3deg) scaleX(1)}  50%{transform:translateY(-5px) rotate(3deg) scaleX(1)} }
@keyframes owlCeleb  { from{transform:translateY(0) rotate(-8deg) scale(1) scaleX(1)} to{transform:translateY(-12px) rotate(8deg) scale(1.05) scaleX(1)} }
@keyframes owlIdle   { 0%,100%{transform:translateY(0) scaleX(1)} 50%{transform:translateY(-6px) scaleX(1)} }
@keyframes owlTeachF { 0%,100%{transform:translateY(0) rotate(2deg) scaleX(-1)}   50%{transform:translateY(-8px) rotate(-2deg) scaleX(-1)} }
@keyframes owlThinkF { 0%,100%{transform:translateY(0) rotate(3deg) scaleX(-1)}   50%{transform:translateY(-5px) rotate(-3deg) scaleX(-1)} }
@keyframes owlCelebF { from{transform:translateY(0) rotate(8deg) scale(1) scaleX(-1)} to{transform:translateY(-12px) rotate(-8deg) scale(1.05) scaleX(-1)} }
@keyframes owlIdleF  { 0%,100%{transform:translateY(0) scaleX(-1)} 50%{transform:translateY(-6px) scaleX(-1)} }

/* Expl messages strip */
.es-expl-messages { display: flex; flex-direction: column; gap: 8px; }

/* No bubble frame inside expl-card */
.es-expl-card .es-bubble-ai { background: transparent; border: none; box-shadow: none; padding: 4px 0; color: #1E3A5F; }
.es-expl-card .es-avatar { display: none; }
.es-expl-card .es-bubble-row { gap: 0; }

/* Waiting card (FIX 4) */
.es-waiting-card {
  display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 32px 20px;
  background: rgba(5,150,105,.04); border: 1px dashed rgba(5,150,105,.18); border-radius: 14px;
}
.es-waiting-dots { display: flex; gap: 6px; align-items: center; }
.es-waiting-dots span { width: 10px; height: 10px; border-radius: 50%; background: #10b981; animation: es-dot 1.2s ease-in-out infinite; }
.es-waiting-dots span:nth-child(2) { animation-delay: .2s; }
.es-waiting-dots span:nth-child(3) { animation-delay: .4s; }

/* Shimmer skeleton (FIX 4) */
.es-shimmer-block { display: flex; flex-direction: column; gap: 10px; padding: 4px 0; }
.es-sh-line {
  height: 14px; border-radius: 7px;
  background: linear-gradient(90deg,#d1fae5 25%,#a7f3d0 50%,#d1fae5 75%);
  background-size: 200% 100%; animation: es-sh-anim 1.4s ease-in-out infinite;
}
@keyframes es-sh-anim { 0%{background-position:100% 0} 100%{background-position:-100% 0} }

/* ── Right panel (FIX 5: chat header + glass bubbles) ── */
.es-chat-panel {
  width: 340px; flex-shrink: 0; display: flex; flex-direction: column;
  background: rgba(248,252,250,.78); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
  border-inline-start: 1px solid rgba(5,150,105,.1);
  overflow: hidden; min-height: 0; height: 100%;
}

/* Chat header (FIX 5) */
.es-chat-header {
  flex-shrink: 0; display: flex; align-items: center; gap: 10px; padding: 10px 14px;
  height: 52px; min-height: 52px; max-height: 52px; box-sizing: border-box;
  background: rgba(255,255,255,.85); backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid rgba(5,150,105,.1);
}
.es-chat-avatar-box {
  width: 32px; height: 32px; border-radius: 10px; flex-shrink: 0;
  background: linear-gradient(135deg,#059669,#10B981);
  display: flex; align-items: center; justify-content: center;
  color: white; font-weight: 800; font-size: 14px; font-family: 'Sora',sans-serif;
}
.es-chat-info { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0; }
.es-chat-name { font-size: 13px; font-weight: 700; color: #065F46; font-family: 'Sora',sans-serif; }
.es-chat-status { display: flex; align-items: center; gap: 5px; font-size: 11px; color: #059669; }
.es-status-dot {
  width: 7px; height: 7px; border-radius: 50%; background: #059669;
  box-shadow: 0 0 0 2px rgba(5,150,105,.2); animation: es-pulse-dot 2s ease-in-out infinite;
}
@keyframes es-pulse-dot { 0%,100%{box-shadow:0 0 0 2px rgba(5,150,105,.2)} 50%{box-shadow:0 0 0 4px rgba(5,150,105,.12)} }
.es-chat-hint-pill {
  font-size: 10px; font-weight: 600; color: #6B7280;
  background: rgba(5,150,105,.06); border: 1px solid rgba(5,150,105,.12);
  border-radius: 100px; padding: 3px 9px; white-space: nowrap; flex-shrink: 0;
}

/* Chat scrollable area */
.es-chat {
  flex: 1 1 0; min-height: 0; box-sizing: border-box;
  overflow-y: scroll; overflow-x: hidden; scroll-behavior: smooth;
  padding: 16px 14px; display: flex; flex-direction: column; gap: 14px; background: transparent;
}
.es-chat::-webkit-scrollbar { width: 4px; }
.es-chat::-webkit-scrollbar-thumb { background: #D1FAE5; border-radius: 3px; }
.es-chat::-webkit-scrollbar-track { background: transparent; }

/* Center states */
.es-center-state { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 16px; padding: 60px 20px; flex: 1; }
.es-wait-text { font-size: 13px; color: #059669; text-align: center; font-weight: 600; }
.es-error-text { font-size: 15px; color: #dc2626; text-align: center; }
.es-retry-btn { background: #059669; color: white; border: none; padding: 10px 24px; border-radius: 12px; font-size: 15px; cursor: pointer; }

/* Spinner */
.es-spinner { width: 40px; height: 40px; border: 3px solid rgba(5,150,105,.2); border-top-color: #059669; border-radius: 50%; animation: es-spin .8s linear infinite; }
@keyframes es-spin { to { transform: rotate(360deg); } }

/* Bubble rows */
.es-bubble-row { display: flex; align-items: flex-end; gap: 8px; animation: es-fadeUp .35s ease both; }
.es-ai-row     { flex-direction: row; }
.es-ai-row-rtl { flex-direction: row-reverse; }
.es-user-row   { flex-direction: row-reverse; }
@keyframes es-fadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }

/* Avatar — 26×26 gradient box, no img */
.es-avatar { width: 26px; height: 26px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.es-avatar-box {
  width: 26px; height: 26px; border-radius: 8px;
  background: linear-gradient(135deg,#059669,#10B981);
  display: flex; align-items: center; justify-content: center;
  color: white; font-weight: 700; font-size: 13px; font-family: 'Sora',sans-serif;
}

/* Bubbles (FIX 6 — glass AI bubble, #1E3A5F text) */
.es-bubble { padding: 10px 14px; font-size: 13px; line-height: 1.65; }
.es-bubble-ai {
  max-width: 88%;
  background: rgba(255,255,255,.85); color: #1E3A5F;
  border-radius: 16px; border-start-start-radius: 4px;
  border: 1px solid rgba(255,255,255,.9);
  box-shadow: 0 2px 12px rgba(5,150,105,.1);
  backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px);
}
.es-bubble-user {
  max-width: 72%;
  background: linear-gradient(135deg,#059669,#047857); color: white;
  border-radius: 16px; border-start-end-radius: 4px;
  box-shadow: 0 3px 0 #047857,0 6px 16px rgba(5,150,105,.20);
}

/* KP bubble (FIX 5) */
.es-kp-bubble { border-inline-end: 3px solid #059669; }
.es-kp-label { font-size: 11px; font-weight: 800; color: #059669; margin-bottom: 6px; letter-spacing: .3px; }

/* System message (FIX 5) */
.es-sys-msg { font-size: 11px; color: #9CA3AF; text-align: center; padding: 4px 0; font-style: italic; }

/* Thinking dots */
.es-thinking { display: flex; gap: 5px; align-items: center; padding: 12px 16px; }
.es-thinking span { width: 7px; height: 7px; border-radius: 50%; background: #10b981; animation: es-dot 1.2s ease-in-out infinite; }
.es-thinking span:nth-child(2) { animation-delay: .2s; }
.es-thinking span:nth-child(3) { animation-delay: .4s; }
@keyframes es-dot { 0%,60%,100%{transform:translateY(0);opacity:.4} 30%{transform:translateY(-6px);opacity:1} }

.es-inline-img { max-width: 100%; border-radius: 12px; display: block; }

.es-img-spinner { display: inline-block; width: 12px; height: 12px; border: 2px solid #D1FAE5; border-top-color: #059669; border-radius: 50%; animation: es-spin 0.7s linear infinite; flex-shrink: 0; }

.es-shimmer-bubble {
  width: 200px; height: 120px;
  background: linear-gradient(90deg,#e2f5ec 25%,#c8eedd 50%,#e2f5ec 75%);
  background-size: 400% 100%; animation: es-sh-anim 1.4s ease infinite; border-radius: 14px;
}

/* Markdown (FIX 4 — term/formula/example) */
.es-md { font-family: Nunito,sans-serif; font-size: 13px; line-height: 1.8; color: #1E3A5F; }
.es-expl-text { font-size: 20px; }
.es-md p { margin: 5px 0; }
.es-md p:first-child { margin-top: 0; }
.es-md p:last-child { margin-bottom: 0; }
.es-md strong { font-weight: 800; color: #059669; background: #F0FDF4; border-radius: 4px; padding: 1px 6px; }
.es-md em { font-style: italic; color: #1E3A5F; }
.es-md ul,.es-md ol { padding-inline-start: 18px; margin: 6px 0; }
.es-md li { margin-bottom: 3px; line-height: 1.7; }
.es-md h1,.es-md h2,.es-md h3 { font-family: Sora,sans-serif; color: #065F46; font-weight: 700; margin: 10px 0 5px; }
.es-md h1{font-size:16px} .es-md h2{font-size:15px} .es-md h3{font-size:14px}
.es-md code { background: #F8F9FA; border: 1px solid #E5E7EB; border-radius: 4px; padding: 1px 5px; font-size: 12px; font-family: 'JetBrains Mono',monospace; color: #1E3A5F; }
.es-md pre { background: #F8F9FA; border: 1px solid #E5E7EB; border-radius: 8px; padding: 10px; overflow-x: auto; }
.es-md pre code { background: none; border: none; padding: 0; }
.es-md blockquote {
  border-inline-end: 3px solid #10B981; padding-inline-end: 12px; margin: 6px 0;
  color: #6B7280; font-style: italic; background: rgba(16,185,129,.04); border-radius: 0 4px 4px 0; padding-block: 4px;
}

/* CLEAR_Q */
.es-clear-q {
  background: rgba(255,255,255,.9); border-radius: 18px; padding: 18px;
  border: 1.5px solid rgba(5,150,105,.15);
  box-shadow: 0 4px 0 rgba(5,150,105,.1),0 8px 24px rgba(5,150,105,.07);
  animation: es-fadeUp .4s ease both; text-align: center;
}
.es-clear-label { font-size: 14px; font-weight: 700; color: #065f46; margin-bottom: 12px; }
.es-clear-btns { display: flex; gap: 8px; justify-content: center; flex-wrap: wrap; }
.es-btn-yes {
  background: #059669; color: white; border: 2px solid #047857; padding: 9px 20px; border-radius: 12px;
  font-size: 13px; font-weight: 700; cursor: pointer;
  box-shadow: 0 4px 0 #047857,0 8px 16px rgba(5,150,105,.20); transition: transform .1s,box-shadow .1s;
}
.es-btn-yes:hover { transform: translateY(-2px); box-shadow: 0 6px 0 #047857,0 12px 20px rgba(5,150,105,.25); }
.es-btn-yes:active { transform: translateY(4px); box-shadow: 0 1px 0 #047857; }
.es-btn-no {
  background: rgba(239,68,68,.08); color: #dc2626; border: 2px solid rgba(239,68,68,.30);
  padding: 9px 20px; border-radius: 12px; font-size: 13px; font-weight: 700;
  cursor: pointer; transition: all .15s; box-shadow: 0 3px 0 rgba(239,68,68,.18);
}
.es-btn-no:hover { background: rgba(239,68,68,.14); box-shadow: 0 4px 0 rgba(239,68,68,.22); }

/* HELP */
.es-understood-wrap { display: flex; justify-content: center; padding: 8px 0; }
.es-understood-btn {
  background: none; border: 1px solid rgba(5,150,105,.3); color: #059669;
  padding: 9px 18px; border-radius: 10px; font-size: 13px; cursor: pointer; transition: background .15s;
}
.es-understood-btn:hover { background: rgba(5,150,105,.06); }

/* FAIL */
.es-fail-bubble { border-inline-start: 3px solid #f59e0b; }
.es-fail-wrap { display: flex; justify-content: center; padding: 8px 0; animation: es-fadeUp .4s ease both; }
.es-retry-explain-btn {
  background: #f59e0b; color: white; border: 2px solid #d97706; padding: 10px 24px; border-radius: 12px;
  font-size: 14px; font-weight: 700; cursor: pointer;
  box-shadow: 0 4px 0 #d97706,0 8px 18px rgba(245,158,11,.20); transition: transform .1s,box-shadow .1s;
}
.es-retry-explain-btn:hover { transform: translateY(-2px); box-shadow: 0 6px 0 #d97706,0 12px 22px rgba(245,158,11,.25); }
.es-retry-explain-btn:active { transform: translateY(4px); box-shadow: 0 1px 0 #d97706; }

/* COMPLETE */
.es-complete-panel {
  display: flex; flex-direction: column; align-items: center;
  gap: 14px; padding: 24px 14px; animation: es-popIn .5s cubic-bezier(.34,1.56,.64,1) both;
}
@keyframes es-popIn { from{opacity:0;transform:scale(.8)} to{opacity:1;transform:scale(1)} }
.es-complete-icon { font-size: 56px; }
.es-complete-score { display: flex; flex-direction: column; align-items: center; gap: 2px; }
.es-score-num { font-size: 38px; font-weight: 800; font-family: 'Sora',sans-serif; line-height: 1; }
.es-score-label { font-size: 11px; color: #6B7280; }
.es-complete-text { font-size: 16px; font-weight: 700; color: #065f46; text-align: center; margin: 0; }
.es-complete-btns { display: flex; gap: 8px; flex-wrap: wrap; justify-content: center; }
.es-next-btn {
  background: #059669; color: white; border: 2px solid #047857; padding: 10px 20px; border-radius: 12px;
  font-size: 13px; font-weight: 700; cursor: pointer;
  box-shadow: 0 4px 0 #047857,0 8px 18px rgba(5,150,105,.20); transition: transform .1s,box-shadow .1s;
}
.es-next-btn:hover { transform: translateY(-2px); box-shadow: 0 6px 0 #047857,0 12px 22px rgba(5,150,105,.25); }
.es-next-btn:active { transform: translateY(4px); box-shadow: 0 1px 0 #047857; }
.es-back-btn-secondary {
  background: rgba(5,150,105,.08); color: #059669; border: 2px solid rgba(5,150,105,.30);
  padding: 10px 18px; border-radius: 12px; font-size: 12px; font-weight: 700;
  cursor: pointer; transition: background .15s,box-shadow .15s; box-shadow: 0 3px 0 rgba(5,150,105,.20);
}
.es-back-btn-secondary:hover { background: rgba(5,150,105,.14); box-shadow: 0 4px 0 rgba(5,150,105,.25); }

/* Chat input bar (FIX 5 — 38×38 send btn, border-radius 12px) */
.es-chat-bar {
  flex-shrink: 0; overflow: hidden; display: flex; align-items: flex-end; gap: 8px;
  padding: 10px 14px 14px;
  background: rgba(255,255,255,.92); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
  border-top: 1px solid rgba(5,150,105,.1);
  height: 70px; min-height: 70px; max-height: 70px; box-sizing: border-box;
}
.es-chat-textarea {
  flex: 1; border: 1.5px solid #d1fae5; border-radius: 12px;
  padding: 9px 12px; font-size: 13px;
  resize: none; outline: none; background: #f9fafb;
  max-height: 140px; overflow-y: auto; transition: border-color .15s;
  font-family: inherit; line-height: 1.5;
}
.es-chat-textarea:focus { border-color: #059669; background: white; }
.es-chat-textarea:disabled { opacity: .6; }
.es-chat-send {
  width: 38px; height: 38px; flex-shrink: 0;
  background: linear-gradient(135deg,#059669,#047857); color: white;
  border: none; border-radius: 12px;
  font-size: 16px; font-weight: 700; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 3px 8px rgba(5,150,105,.3); transition: opacity .15s,transform .15s;
}
.es-chat-send:hover:not(:disabled) { transform: scale(1.1) rotate(-5deg); }
.es-chat-send:disabled { opacity: .45; cursor: default; transform: none; }

/* XP float (FIX 5) */
.es-xp-float {
  position: fixed; top: 60px; left: 50%; transform: translateX(-50%);
  background: linear-gradient(135deg,#F59E0B,#D97706); color: white;
  font-weight: 800; font-size: 16px; padding: 8px 20px; border-radius: 100px;
  box-shadow: 0 4px 20px rgba(245,158,11,.4); z-index: 500; pointer-events: none;
  animation: es-xp-fly 1.4s ease-out both;
}
@keyframes es-xp-fly {
  0%  {opacity:0;transform:translateX(-50%) translateY(0) scale(.8)}
  20% {opacity:1;transform:translateX(-50%) translateY(0) scale(1.1)}
  70% {opacity:1;transform:translateX(-50%) translateY(-20px) scale(1)}
  100%{opacity:0;transform:translateX(-50%) translateY(-40px) scale(.9)}
}

/* Image assets */
.es-img-thumb {
  display: block; width: 100%; max-width: 200px; max-height: 150px; object-fit: cover;
  border-radius: 12px; margin-top: 8px; border: 2px solid #D1FAE5;
  box-shadow: 0 5px 0 rgba(5,150,105,.15),0 10px 24px rgba(5,150,105,.12);
  cursor: zoom-in; transition: all 0.2s ease;
}
.es-img-thumb:hover { border-color: #059669; box-shadow: 0 7px 0 rgba(5,150,105,.2),0 14px 32px rgba(5,150,105,.18); transform: translateY(-2px); }
.es-img-caption { font-size: 10px; color: #6B7280; font-family: Nunito,sans-serif; text-align: center; margin-top: 4px; margin-bottom: 0; }

/* Lightbox */
.es-lightbox {
  position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 1000;
  background: rgba(0,0,0,.85); backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center; animation: es-lb-fade .2s ease;
}
@keyframes es-lb-fade { from{opacity:0} to{opacity:1} }
.es-lightbox-close {
  position: absolute; top: 16px; inset-inline-end: 16px;
  width: 40px; height: 40px; border-radius: 50%;
  background: rgba(255,255,255,.15); border: 1.5px solid rgba(255,255,255,.3);
  color: white; font-size: 18px; cursor: pointer;
  display: flex; align-items: center; justify-content: center; transition: background .15s;
}
.es-lightbox-close:hover { background: rgba(255,255,255,.25); }
.es-lightbox-inner { display: flex; flex-direction: column; align-items: center; }
.es-lightbox-img {
  max-width: 90vw; max-height: 85vh; object-fit: contain;
  border-radius: 16px; box-shadow: 0 24px 80px rgba(0,0,0,.5);
  animation: es-lb-zoom .25s cubic-bezier(.34,1.56,.64,1);
}
@keyframes es-lb-zoom { from{transform:scale(.8);opacity:0} to{transform:scale(1);opacity:1} }
.es-lightbox-caption { color: rgba(255,255,255,.7); font-size: 13px; font-family: Nunito,sans-serif; text-align: center; margin-top: 12px; }

/* Pulse animation (used by owl speech bubble during streaming) */
@keyframes es-pill-pulse {
  0%,100% { opacity: 1; }
  50%      { opacity: .5; }
}

/* FIX 4 — Inline image inside bubble */
.es-img-inline {
  width: 100%; max-height: 220px; border-radius: 12px;
  border: 1px solid #E5E7EB; margin-top: 12px; overflow: hidden;
}
.es-img-inline-shimmer {
  width: 100%; height: 160px; border-radius: 12px; margin-top: 12px;
  background: linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%);
  background-size: 200% 100%; animation: es-shimmer 1.4s infinite linear;
}

/* FIX 3 — Explanation image frame */
.es-expl-image-frame {
  width: 100%; border-radius: 14px; border: 1px solid #E5E7EB;
  overflow: hidden; background: #F9FAFB; min-height: 160px;
  display: flex; align-items: center; justify-content: center;
}
.es-expl-image-shimmer {
  width: 100%; height: 160px;
  background: linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%);
  background-size: 200% 100%; animation: es-shimmer 1.4s infinite linear;
}

/* FIX 5 — Key terms panel */
.es-key-terms-panel {
  padding: 12px 16px 4px; border-top: 1px solid rgba(5,150,105,.08);
}
.es-key-terms-header {
  font-family: 'Sora',sans-serif; font-size: 12px; color: #6B7280;
  font-weight: 600; margin-bottom: 8px;
}
.es-key-terms-pills {
  display: flex; flex-wrap: wrap; gap: 6px; padding-bottom: 12px;
}
.es-key-term-pill {
  background: #F0FDF4; border: 1px solid #D1FAE5; color: #059669;
  font-size: 12px; padding: 3px 10px; border-radius: 20px;
  font-family: 'Nunito',sans-serif; font-weight: 600;
}

/* Poem display panel */
.es-poem-panel {
  border-top: 1px solid rgba(5,150,105,.08);
  position: relative; z-index: 6; /* paint above es-owl-speech (z-index:5) within es-expl-card stacking context */
}
.es-poem-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 16px; cursor: pointer; user-select: none;
  font-family: 'Sora',sans-serif; font-size: 13px; font-weight: 600; color: #059669;
  direction: rtl;
}
.es-poem-header:hover { background: rgba(5,150,105,.04); border-radius: 0 0 4px 4px; }
.es-poem-title-text { flex: 1; }
.es-poem-chevron { font-size: 10px; transition: transform .2s; margin-inline-start: 8px; }
.es-poem-chevron.open { transform: rotate(180deg); }
.es-poem-verses {
  padding: 4px 16px 14px; display: flex; flex-direction: column; gap: 10px;
}
.es-poem-verse-row {
  display: grid; grid-template-columns: 1fr auto 1fr; align-items: center;
  gap: 8px; direction: rtl; font-family: 'Sora',sans-serif;
  font-size: 14px; color: #1F2937; line-height: 1.8;
}
.es-poem-hemistich.right { text-align: right; }
.es-poem-hemistich.left  { text-align: left; }
.es-poem-verse-full {
  text-align: center; font-family: 'Sora',sans-serif;
  font-size: 14px; color: #1F2937; line-height: 1.8; direction: rtl;
}
.es-poem-divider {
  display: inline-block; width: 1px; height: 18px;
  background: rgba(5,150,105,.25); flex-shrink: 0;
}

/* Responsive — mobile */
@media (max-width: 767px) {
  .es-panels { display: flex; flex-direction: column; }
  .es-expl-panel { border-inline-start: none; border-bottom: 1px solid rgba(5,150,105,.08); max-height: 48vh; padding-bottom: 16px; }
  .es-owl-teacher { display: none; }
  .es-chat-panel { width: 100%; flex: 1; border-inline-start: none; }
  .es-header { padding: 0 10px; height: auto; min-height: 44px; max-height: none; }
  .es-logo-text { display: none; }
  .es-lang-btn   { font-size: 11px; padding: 3px 8px; }
  .es-avatar     { width: 26px; height: 26px; }
  .es-chat       { padding: 10px; font-size: 13px; }
  .es-bubble     { padding: 9px 11px; font-size: 13px; }
  .es-bubble-ai  { max-width: 92%; }
  .es-bubble-user { max-width: 90%; }
  .es-chat-bar { height: auto; min-height: 52px; max-height: none; padding: 8px 10px; }
  .es-chat-textarea { font-size: 13px; }
  .es-img-thumb { max-width: 100%; max-height: none; }
  .es-complete-panel { padding: 16px 10px; gap: 10px; }
  .es-complete-icon  { font-size: 42px; }
  .es-complete-text  { font-size: 14px; }
}

/* Responsive — tablet */
@media (min-width: 768px) and (max-width: 1024px) {
  .es-panels { grid-template-columns: 1fr 300px; }
  .es-owl-teacher { width: 120px; }
  .es-owl-img { width: 120px; }
  .es-bubble-ai   { max-width: 88%; }
  .es-bubble-user { max-width: 86%; }
}

/* Multi-topic progress strip (Phase A.1) */
.es-topics-progress-strip {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 20px;
  background: rgba(255,255,255,0.7);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid rgba(229,231,235,0.6);
  position: relative;
  z-index: 5;
}
.es-tps-label {
  display: flex;
  align-items: center;
  gap: 12px;
}
.es-tps-count {
  font-size: 12px;
  font-weight: 700;
  color: #6B7280;
  white-space: nowrap;
  font-family: 'Sora', sans-serif;
}
.es-tps-bar {
  flex: 1;
  height: 6px;
  background: #F3F4F6;
  border-radius: 4px;
  overflow: hidden;
}
.es-tps-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #059669, #10B981);
  border-radius: 4px;
  transition: width 0.4s ease;
}
.es-tps-pills {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.es-tps-pill {
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 20px;
  white-space: nowrap;
  font-family: 'Sora', sans-serif;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.es-tps-pill.done    { background: #D1FAE5; color: #059669; }
.es-tps-pill.active  { background: linear-gradient(135deg,#059669,#10B981); color: white; box-shadow: 0 2px 8px rgba(5,150,105,.3); }
.es-tps-pill.pending { background: #F3F4F6; color: #9CA3AF; }
@media (max-width: 768px) {
  .es-topics-progress-strip { padding: 8px 14px; }
  .es-tps-pill { max-width: 120px; }
}

.es-final-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 32px;
  background: linear-gradient(135deg, #FEF9EE 0%, #F0FDF4 100%);
  border: 2px solid #F59E0B;
  border-radius: 20px;
  text-align: center;
  max-width: 520px;
  margin: 40px auto;
}
.es-final-icon { font-size: 48px; margin-bottom: 12px; }
.es-final-title {
  font-size: 20px;
  font-weight: 800;
  color: #1E3A5F;
  font-family: 'Sora', sans-serif;
  margin-bottom: 20px;
}
.es-final-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  width: 100%;
  margin-bottom: 20px;
}
.es-final-stat {
  background: white;
  border-radius: 12px;
  padding: 14px 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,.05);
}
.es-final-stat-value {
  font-size: 22px;
  font-weight: 800;
  font-family: 'Sora', sans-serif;
  color: #059669;
}
.es-final-stat-label {
  font-size: 11px;
  color: #6B7280;
  margin-top: 4px;
  font-family: 'Nunito', sans-serif;
}
`;

